import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  RefreshCw, Save, RotateCcw, Info, AlertTriangle, Plus, Trash2, Undo2, Search,
  Cpu, Gauge, Server, ListOrdered, FileText, SlidersHorizontal, ChevronLeft, ChevronRight, ChevronDown,
  MessageSquareText, FlaskConical, X, DownloadCloud, Download, Upload, Check, Pencil, Eye,
} from 'lucide-react';
import { EFFORT_TIERS, SHAPES, forShape, toggleTier } from './reasoningShape';
import { SIGNS, applied, applySelection, bySign, fileNameFor, groupFileBody, readGroupFile, sameRows,
  signOf, summarise, toggleRow } from './settingsFile';
import { downloadJson } from '../utils/downloadJson';
import { withMeasured } from './measured';
import { getSettings, upsertSetting, deleteSetting, changeSection, getAppConfig, previewReasoning, testReasoning, testAudio, modelHosts, refreshCatalog, resolveModels, setModelsDev } from '../api';



const clone = (o) => JSON.parse(JSON.stringify(o ?? {}));

function coerce(text, type) {
  if (type === 'multiselect') return Array.isArray(text) ? text : [];
  if (type === 'bool') return text === true || ['1', 'true', 'yes', 'on'].includes(String(text).toLowerCase());
  if (type === 'int') { const n = parseInt(text, 10); return Number.isNaN(n) ? 0 : n; }
  if (type === 'float') { const n = parseFloat(text); return Number.isNaN(n) ? 0 : n; }
  if (type === 'profile') return text && typeof text === 'object' ? text : {};
  if (type === 'json') { if (text && typeof text === 'object') return text; const t = String(text ?? '').trim(); return t ? JSON.parse(t) : {}; }
  return text;
}

function JsonCell({ value, onChange, disabled = false, disabledHint }) {
  const [txt, setTxt] = useState(value && Object.keys(value).length ? JSON.stringify(value) : '');
  const [err, setErr] = useState(false);
  useEffect(() => { setTxt(value && Object.keys(value).length ? JSON.stringify(value) : ''); }, [JSON.stringify(value)]);
  const commit = () => {
    const t = txt.trim();
    if (!t) { setErr(false); onChange({}); return; }
    try { onChange(JSON.parse(t)); setErr(false); } catch { setErr(true); }
  };
  return (
    <input value={txt} placeholder="{ }" disabled={disabled} title={disabled ? disabledHint : undefined}
      onChange={(e) => setTxt(e.target.value)} onBlur={commit}
      className={`w-56 px-2 py-1 bg-field border rounded text-fg text-xs font-mono focus:border-orange disabled:opacity-40 ${err ? 'border-red-500' : 'border-field-border'}`} />
  );
}

// Lists every registered key, set or not: a host with no id is the thing worth
// seeing. Keys are fixed — a free-form key let one row take another's host ids.
function MapCell({ value, keyOptions = [], onChange, disabled = false, disabledHint,
                   keyLabel = 'key', valueLabel = 'value' }) {
  const map = value && typeof value === 'object' ? value : {};
  const set = keyOptions.filter((k) => map[k]);
  const [box, setBox] = useState(null);
  const trigger = useRef(null);
  const pop = useRef(null);
  const open = box !== null;

  // Fixed, not absolute: the sticky first column's stacking context paints over an
  // absolutely placed panel, clipping the half of it that holds the host names.
  const place = () => {
    const r = trigger.current?.getBoundingClientRect();
    if (!r) return;
    const width = Math.min(512, window.innerWidth - 16);
    const top = r.bottom + 4;
    setBox({ width, top,
             left: Math.max(8, Math.min(r.right - width, window.innerWidth - width - 8)),
             maxHeight: Math.max(180, window.innerHeight - top - 12) });
  };
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (trigger.current?.contains(e.target) || pop.current?.contains(e.target)) return;
      setBox(null);
    };
    document.addEventListener('mousedown', onDown);
    // `true` to catch the table container's scroll, not only the window's.
    window.addEventListener('scroll', place, true);
    window.addEventListener('resize', place);
    return () => {
      document.removeEventListener('mousedown', onDown);
      window.removeEventListener('scroll', place, true);
      window.removeEventListener('resize', place);
    };
  }, [open]);

  // Blank means "not set here", so it is dropped rather than stored as "".
  const write = (k, v) => {
    const next = { ...map };
    if (v.trim()) next[k] = v; else delete next[k];
    onChange(next);
  };

  if (disabled) {
    return <div title={disabledHint || 'Not applicable'}
      className="w-56 px-2 py-1 bg-field/40 border border-field-border rounded text-xs text-fg-muted italic opacity-60 cursor-not-allowed select-none">n/a</div>;
  }
  return (
    <div className="relative">
      <button type="button" ref={trigger} onClick={() => (open ? setBox(null) : place())}
        className="flex w-56 items-center justify-between gap-1 rounded border border-field-border bg-field px-2 py-1 text-xs text-fg hover:border-orange">
        <span className={set.length ? '' : 'text-fg-muted italic'}>
          {set.length ? `${set.length} of ${keyOptions.length} hosts` : 'none set'}
        </span>
        <ChevronDown className="h-3 w-3 shrink-0 text-fg-muted" />
      </button>
      {open && (
        <div ref={pop} style={{ left: box.left, top: box.top, width: box.width,
                                maxHeight: box.maxHeight }}
          className="fixed z-[70] overflow-auto rounded border border-panel-border bg-panel p-2 shadow-xl">
          <div className="mb-1 flex gap-2 px-1 text-[10px] uppercase tracking-wide text-fg-muted">
            <span className="w-44">{keyLabel}</span><span>{valueLabel}</span>
          </div>
          {keyOptions.map((k) => (
            <div key={k} className="mb-1 flex items-center gap-2">
              <span className={`w-44 shrink-0 truncate px-2 py-1 font-mono text-xs ${map[k] ? 'text-fg' : 'text-fg-muted'}`}>{k}</span>
              <input value={map[k] || ''} onChange={(e) => write(k, e.target.value)}
                className={`flex-1 rounded border bg-field px-2 py-1 font-mono text-xs focus:border-orange ${
                  map[k] ? 'border-field-border text-fg' : 'border-field-border/50 text-fg-muted'}`} />
            </div>
          ))}
          <p className="mt-1 px-1 text-[11px] text-fg-muted">
            Left blank, this model is not configured for that host — selecting it is refused
            rather than sent under a guessed id.
          </p>
        </div>
      )}
    </div>
  );
}

// Native select on purpose: a popover here is clipped by the sticky provider
// column's stacking context. Choices are hosts with an id — others cannot serve.
function ServedAsCell({ value, hostIds, providers = [], onChange, disabled = false, disabledHint }) {
  const ids = hostIds && typeof hostIds === 'object' ? hostIds : {};
  // Only hosts that still exist: a stored `host_id` outlives its provider, and offering it
  // routed nowhere. The id stays on the row.
  const declared = Object.keys(ids)
    .filter((k) => ids[k] && (!providers.length || providers.includes(k))).sort();
  // A new row has no hosts yet, so offer every provider until one id exists.
  const choices = declared.length ? declared : providers;
  // Any value not on offer: a controlled <select> shows its first option for a value it lacks,
  // so a model on a deleted provider read as served by `anthropic`. "Not in Providers" rather
  // than "deleted": the router also builds a few from its environment (groq, vertex) that route
  // without a row here.
  const orphan = value && !choices.includes(value);
  const unlisted = orphan && providers.length > 0 && !providers.includes(value);

  if (disabled) {
    return <div title={disabledHint || 'Not applicable'}
      className="w-40 px-2 py-1 bg-field/40 border border-field-border rounded text-xs text-fg-muted italic opacity-60 cursor-not-allowed select-none">n/a</div>;
  }
  return (
    <div>
    <div className="flex items-center gap-1">
      <select value={value || ''} onChange={(e) => onChange(e.target.value)}
        title={unlisted ? `'${value}' is not in Settings → Providers`
          : orphan ? `'${value}' has no id under Hosts, so it cannot serve this model`
          : !value && declared.length ? 'Pick the host this model is published under'
          : declared.length ? `Published as ${value}/… — one of ${declared.length} host${declared.length === 1 ? '' : 's'} with an id`
          : 'No host ids yet — add one under Hosts'}
        className={`w-40 shrink-0 rounded border bg-field px-2 py-1 text-xs text-fg focus:border-orange ${
          orphan ? 'border-amber-500/70' : 'border-field-border'}`}>
        {/* `_default` is the one row legitimately without a provider; for every
            other, empty means nothing routes it — so once a host is picked, there
            is no way back to that. */}
        {!value && <option value="">— pick a host —</option>}
        {orphan && <option value={value}>{value} — {unlisted ? 'not in Providers' : 'no id'}</option>}
        {choices.map((host) => <option key={host} value={host}>{host}</option>)}
      </select>
      {orphan && <AlertTriangle className="h-3 w-3 shrink-0 text-red-400" />}
    </div>
    {/* At the cell, not on save: a rejection two clicks later cannot say which
        of forty rows caused it. */}
    {orphan && (
      <p className="mt-0.5 w-40 text-[10px] leading-tight text-red-400">
        {unlisted
          ? <><span className="font-mono">{value}</span> is not in Providers — add it there, or pick another host.</>
          : <>No id for <span className="font-mono">{value}</span> — set one under Hosts first.</>}
      </p>
    )}
    </div>
  );
}

// Value is an array of ids; an empty array means no restriction, not "none".
function MultiSelectCell({ value, options, recommended = [], onChange, disabled = false, disabledHint }) {
  const selected = Array.isArray(value) ? value : [];
  const sel = new Set(selected);
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  // Shared by every cell, so opening one popover closes any other first.
  useEffect(() => {
    if (!open) return;
    const onDown = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  const toggle = (opt) => {
    const next = new Set(sel);
    if (next.has(opt)) next.delete(opt); else next.add(opt);
    onChange(options.filter((o) => next.has(o))); // keep canonical option order
  };
  const summary = selected.length === 0 ? 'All allowed' : `${selected.length} of ${options.length}`;
  if (disabled) {
    return (
      <div title={disabledHint || 'Not applicable'}
        className="w-40 px-2 py-1 bg-field/40 border border-field-border rounded text-xs text-fg-muted italic opacity-60 cursor-not-allowed select-none">
        n/a
      </div>
    );
  }
  return (
    <div className="relative inline-block" ref={ref}>
      <button type="button" onClick={() => setOpen((v) => !v)}
        className="w-40 px-2 py-1 bg-field border border-field-border rounded text-xs flex items-center justify-between hover:border-orange">
        <span className={selected.length === 0 ? 'text-fg-muted italic' : 'text-fg'}>{summary}</span>
        <ChevronDown className="w-3 h-3 text-fg-muted shrink-0" />
      </button>
      {open && (
        <div className="absolute z-20 mt-1 w-60 max-h-72 overflow-auto bg-panel border border-panel-border rounded-lg shadow-lg p-2">
          <div className="flex items-center gap-3 pb-2 mb-1 border-b border-panel-border text-[11px]">
            {recommended.length > 0 && <button type="button" onClick={() => onChange([...recommended])} className="text-orange hover:underline">Recommended</button>}
            <button type="button" onClick={() => onChange([...options])} className="text-fg-muted hover:text-fg">All</button>
            <button type="button" onClick={() => onChange([])} className="text-fg-muted hover:text-fg">Clear</button>
          </div>
          {options.map((o) => (
            <label key={o} className="flex items-center gap-2 px-1 py-0.5 text-xs text-fg cursor-pointer hover:bg-field rounded">
              <input type="checkbox" checked={sel.has(o)} onChange={() => toggle(o)} className="accent-orange" />
              <span className="font-mono">{o}</span>
            </label>
          ))}
        </div>
      )}
    </div>
  );
}

function cellText(v, type) {
  if (v === null || v === undefined) return type === 'bool' ? false : '';
  return v;
}

// models.dev ships 203 providers, too many for a <select>. An unmatched current
// value stays shown: a host the catalog never heard of is a legitimate row.
function SearchSelect({ value, options, disabled, title, onChange }) {
  // `null` means "not typing"; "" is a search that matched nothing yet.
  const [query, setQuery] = useState(null);
  const [box, setBox] = useState(null);
  const input = useRef(null);
  const open = box !== null;
  const text = query ?? value ?? '';
  // Uncapped: a capped list leaves the provider you wanted absent with nothing
  // saying so.
  const needle = (query ?? '').toLowerCase();
  const matches = needle
    ? options.filter((o) => o.toLowerCase().includes(needle))
    : options;

  // Fixed to the viewport, measured off the input: the group's horizontal scroll
  // container clips an absolutely positioned child at its edges.
  const place = () => {
    const r = input.current?.getBoundingClientRect();
    if (r) setBox({ left: r.left, top: r.bottom + 4, width: r.width });
  };
  useEffect(() => {
    if (!open) return undefined;
    // `true` to catch scrolls on the table container, not just the window.
    window.addEventListener('scroll', place, true);
    window.addEventListener('resize', place);
    return () => {
      window.removeEventListener('scroll', place, true);
      window.removeEventListener('resize', place);
    };
  }, [open]);

  const pick = (o) => { onChange(o); setQuery(null); setBox(null); };

  // One flat list so the arrow keys and the rendering agree on what index 0 is:
  // `— none —` is an option, and skipping it made Up from the first match a no-op.
  const items = (value ? [''] : []).concat(matches);
  const [active, setActive] = useState(0);
  const list = useRef(null);
  useEffect(() => { setActive(0); }, [needle, open]);
  useEffect(() => {
    if (open) list.current?.children?.[active]?.scrollIntoView({ block: 'nearest' });
  }, [active, open]);

  // Typing filtered the list and then left it mouse-only.
  const onKeyDown = (e) => {
    if (!open) {
      if (e.key === 'ArrowDown') { setQuery(value || ''); place(); }
      return;
    }
    if (e.key === 'ArrowDown') { e.preventDefault(); setActive((i) => Math.min(i + 1, items.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActive((i) => Math.max(i - 1, 0)); }
    // Clamped: a keystroke narrows the list one render before the reset effect runs,
    // so `active` can point past the end and `pick(undefined)` would blank the field.
    else if (e.key === 'Enter' && items.length) {
      e.preventDefault(); pick(items[Math.min(active, items.length - 1)]);
    }
    else if (e.key === 'Escape') { e.preventDefault(); setBox(null); setQuery(null); }
  };

  return (
    <>
      <input ref={input} value={text} disabled={disabled} title={title} placeholder="—"
        onChange={(e) => { setQuery(e.target.value); place(); }}
        onFocus={(e) => { setQuery(value || ''); place(); e.target.select(); }}
        onKeyDown={onKeyDown}
        // Delayed: a blur firing before the click swallows the selection.
        onBlur={() => setTimeout(() => { setBox(null); setQuery(null); }, 150)}
        className="w-full px-2 py-1 bg-field border border-field-border rounded text-fg text-xs focus:border-orange disabled:opacity-40" />
      {open && !disabled && (
        <div ref={list} style={{ left: box.left, top: box.top, width: box.width }}
          className="fixed z-50 max-h-64 overflow-auto rounded border border-field-border bg-panel shadow-lg">
          {items.length === 0 ? (
            <div className="px-2 py-1 text-xs text-muted">no provider matches</div>
          ) : items.map((o, i) => (
            <button key={o || '\u2014none\u2014'} type="button"
              onMouseDown={(e) => e.preventDefault()} onClick={() => pick(o)}
              onMouseEnter={() => setActive(i)}
              className={`block w-full px-2 py-1 text-left text-xs ${i === active ? 'bg-field' : ''} `
                + (o === '' ? 'text-muted' : o === value ? 'text-orange' : 'text-fg')}>
              {o === '' ? '— none —' : o}
            </button>
          ))}
        </div>
      )}
    </>
  );
}

/** The audio packages a provider row may pick, each labelled with what it serves. A vendor
 *  models.dev knows (Azure, OpenAI, Google…) serves its own audio and is offered none: listing
 *  Deepgram under Azure offered a package that cannot reach Azure. A row for an audio provider is
 *  offered its own package; a row naming no vendor yet, every one, to pick from. */
export function audioChoices(audioPackages, offered, short, vendor, vendorKnown) {
  const taken = new Set(offered.map((e) => e.value));
  const all = Object.entries(audioPackages || {}).filter(([npm]) => !taken.has(npm));
  const own = all.filter(([npm]) => vendor && [`@ai-sdk/${vendor}`, `${vendor}-ai-sdk`].includes(npm));
  const shown = vendorKnown ? [] : own.length ? own : vendor ? [] : all;
  return shown
    .sort(([a], [b]) => short(a).localeCompare(short(b)))
    .map(([npm, kinds]) => ({ value: npm, label: `${short(npm)} — ${kinds.join(', ')}` }));
}

function optionsFor(col, dynamicOptions = {}) {
  const arr = col.options || (col.options_from ? dynamicOptions[col.options_from] : null);
  return arr && arr.length ? arr : null;
}

function cellGate(col, values) {
  if (!col.enabled_when) return null;
  if (cellText(values[col.enabled_when], 'bool')) return null;
  return col.disabled_hint || `Enable "${col.enabled_when}" first`;
}

// Whether this install reads models.dev. Off, nothing on this page offers or cites it.
const ModelsDevContext = createContext(true);

// Off, the vendor column names a models.dev provider, and these helps cite it.
const OFF_HELP = {
  sdk_npm: 'Which AI SDK package, and which of its APIs, this host speaks. Every package this build has is offered. Blank lets the service choose.',
  modality: 'What this model is for, and so which endpoint serves it: chat on /v1/chat/completions, transcription and speech on /v1/audio/*, live on /v1/realtime. A call on any other endpoint is refused by name. It also decides what the row must state before it saves — a transcription model its price per minute, not a context window. Unset: chat — set it for every audio model.',
};
// Ends every message about what a row must state because models.dev is off (catalog_source.OFF_NOTE):
// those are to-dos, not faults. Anything else keeps its warning tone.
const MODELS_DEV_OFF = 'models.dev is off';
const isToDo = (message) => String(message).trim().endsWith(MODELS_DEV_OFF);
const shownColumns = (columns, modelsDev) => (modelsDev ? columns
  : columns.filter((c) => c.options_from !== 'catalog_namespaces')
    .map((c) => (OFF_HELP[c.field] ? { ...c, help: OFF_HELP[c.field] } : c)));

// An unset box is drawn plain, not indeterminate: the dash renders FILLED in the
// accent colour, so "nobody has said" came out looking more set than a ticked box.
function BoolCell({ value, disabled, title, onChange }) {
  const modelsDev = useContext(ModelsDevContext);
  const unset = value === undefined || value === null;
  return (
    <input type="checkbox" disabled={disabled}
      title={!unset ? title : modelsDev
        ? 'Not set \u2014 models.dev answers this one. Tick it only to overrule the catalog.'
        : 'Not set \u2014 the built-in default applies. Tick it to state it here.'}
      checked={!!cellText(value, 'bool')}
      onChange={(e) => onChange(e.target.checked)}
      className="accent-orange disabled:opacity-30" />
  );
}

// `high` where offered, else the strongest rung below it, else the weakest: for the 2.8% of
// ladders with no `high`, the one answer that is not the most expensive.
function preferredDefault(offered) {
  if (offered.includes('high')) return 'high';
  const below = EFFORT_TIERS.slice(0, EFFORT_TIERS.indexOf('high'));
  const under = below.filter((t) => offered.includes(t));
  return under.length ? under[under.length - 1] : offered[0];
}

// Only columns that name a default (`rpm` today); everything else stays absent, since a
// seeded `false`/`[]`/`{}` outranks the catalog and reads as conflicts nobody typed.
function initialCells(columns) {
  return Object.fromEntries(columns
    .filter((c) => c.default !== undefined)
    .map((c) => [c.field, c.default]));
}

// Read-only summary; editing happens in the panel. `declaredFor` comes from the
// row, not the reasoning block — it is a property of the row.
function ProfileCell({ value, provider, declaredFor, onOpen, disabled = false, disabledHint }) {
  const reasoning = value && typeof value === 'object' ? value : {};
  const shape = reasoning.mode || null;
  const levels = reasoning.effort || [];

  const summary = shape
    ? (shape === 'effort' && levels.length ? levels.join(', ') : shape)
    : 'Configure…';
  // Flagged in the table too: the provider switch next door invalidates this.
  const stale = declaredFor && provider && declaredFor !== provider;

  return (
    <button type="button" onClick={onOpen} disabled={disabled}
      title={disabled ? disabledHint
        : stale ? `Declared for ${declaredFor}, not ${provider} — open to re-check`
        : 'Hosts, ids, per-host differences and the reasoning ladder'}
      className="flex w-full items-center justify-between gap-2 rounded border border-field-border bg-field px-2 py-1 text-left text-xs hover:border-orange disabled:cursor-not-allowed disabled:opacity-40">
      <span className={shape ? 'font-mono text-fg' : 'text-orange'}>{summary}</span>
      {stale && <AlertTriangle className="h-3 w-3 shrink-0 text-amber-400" />}
    </button>
  );
}

// Its own door: the reasoning column is gated on `supports_reasoning`, and a model that does
// not reason still has per-host prices. No "edited" badge: shipped corrections look typed.
function PricesCell({ row, providers = [], onOpen }) {
  const ids = row.host_id && typeof row.host_id === 'object' ? row.host_id : {};
  // Current providers only, once they are known: an id left under a deleted one counted as a
  // host, so a model nothing routes read "1 of 14 host".
  const served = providers.length ? providers.filter((p) => ids[p]).length
    : Object.values(ids).filter(Boolean).length;
  const total = providers.length;
  const bands = Array.isArray(row.tiers) ? row.tiers.length : 0;

  const note = bands ? `${bands} band${bands === 1 ? '' : 's'}` : '';

  return (
    <button type="button" onClick={onOpen}
      title="Ids, per-host prices, limits and context bands for this model"
      className="flex w-full items-center justify-between gap-2 rounded border border-field-border bg-field px-2 py-1 text-left text-xs hover:border-orange">
      <span className={served ? 'font-mono text-fg' : 'text-fg-muted'}>
        {served ? `${served}${total ? ` of ${total}` : ''} host${served === 1 ? '' : 's'}` : 'none set'}
      </span>
      {note
        ? <span className="shrink-0 text-[10px] text-orange">{note}</span>
        : <ChevronDown className="h-3 w-3 shrink-0 text-fg-muted" />}
    </button>
  );
}

// One renderer for every column type, shared by the table body and AddRow.
function GroupCell({ col, values, dynamicOptions, onChange, onOpenPanel }) {
  const value = values[col.field];
  const hint = cellGate(col, values);
  const disabled = hint !== null;
  const opts = optionsFor(col, dynamicOptions);
  const title = disabled ? hint : undefined;

  if (col.type === 'multiselect') {
    return <MultiSelectCell value={value} options={opts || []} recommended={col.recommended || []}
      disabled={disabled} disabledHint={hint} onChange={(arr) => onChange(arr, 'multiselect')} />;
  }
  if (col.type === 'bool') {
    return <BoolCell value={value} disabled={disabled} title={title}
      onChange={(v) => onChange(v, 'bool')} />;
  }
  // One of a fixed set; blank is "not set", which the router resolves.
  if (col.type === 'enum') {
    return (
      <select value={value ?? ''} disabled={disabled} title={title ?? col.help}
        onChange={(e) => onChange(e.target.value || undefined, 'enum')}
        className="rounded border border-field-border bg-field px-1.5 py-1 text-xs text-fg disabled:opacity-40">
        <option value="">not set</option>
        {(opts || []).map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    );
  }
  if (col.type === 'served_as') {
    return <ServedAsCell value={value} hostIds={values.host_id}
      providers={optionsFor(col, dynamicOptions) || []}
      disabled={disabled} disabledHint={hint} onChange={(v) => onChange(v, 'string')} />;
  }
  if (col.type === 'map') {
    return <MapCell value={value || {}} keyOptions={optionsFor({ options_from: col.key_options_from }, dynamicOptions) || []}
      keyLabel={col.key_label} valueLabel={col.value_label}
      disabled={disabled} disabledHint={hint} onChange={(o) => onChange(o, 'json')} />;
  }
  if (col.type === 'json') {
    return <JsonCell value={value || {}} disabled={disabled} disabledHint={hint}
      onChange={(o) => onChange(o, 'json')} />;
  }
  if (col.type === 'profile') {
    return <ProfileCell value={value} provider={values.provider} declaredFor={values.declared_for}
      disabled={disabled} disabledHint={hint} onOpen={() => onOpenPanel && onOpenPanel()} />;
  }
  if (col.type === 'prices') {
    return <PricesCell row={values} providers={dynamicOptions.providers || []}
      onOpen={() => onOpenPanel && onOpenPanel()} />;
  }
  // The vendor's protocols, only where there is a choice, from what its own packages publish.
  if (col.type === 'protocol') {
    const vendor = values.catalog_namespace;
    // models.dev off: no vendor to narrow by, so every package this build has is offered.
    const choices = dynamicOptions.models_dev === false ? (dynamicOptions.sdk_protocols_all || [])
      : vendor ? (dynamicOptions.sdk_protocols?.[vendor] || []) : [];
    // Counted below in APIs, not packages: `@ai-sdk/openai` is one package speaking three
    // (`/responses`, `/chat/completions`, `/completions`), and gating on the package count
    // hid that choice — which is why a second provider row was the only way to say which.
    const short = (n) => n.replace('@ai-sdk/', '').replace('@openrouter/', '');
    // A package name does not say which API it speaks, and `/mantle` speaks two — so
    // each option carries the wire path the service measured it asking for.
    const api = (p) => p.replace(/^\/v\d+\//, '').replace('probe-model', '{id}');
    // One entry per API, not per package; the value carries the probe's method so the pick
    // replays as `sdk[via](id)`.
    // `/v1/completions` is the pre-chat text API. Every openai-compatible reseller reports
    // it, so counting it offered 180 vendors a second option that would break the row.
    const relic = (path) => (path || '').endsWith('/completions')
                            && !(path || '').endsWith('/chat/completions');
    const entries = choices.flatMap((o) => {
      const speaks = (o.speaks || []).filter((s) => !relic(s.path));
      if (!speaks.length) return [{ value: o.npm, label: short(o.npm) }];
      return speaks.map((s) => ({
        value: s.via ? `${o.npm}#${s.via}` : o.npm,
        label: `${short(o.npm)} — ${api(s.path)}`,
      }));
    });
    // Every audio package this build has, by what it serves: none is a models.dev vendor, so a
    // Deepgram or Sarvam row had nothing to pick. Their prices are entered on the model.
    const audio = audioChoices(dynamicOptions.sdk_audio_packages, entries, short, vendor,
                               Boolean(vendor && dynamicOptions.sdk_protocols?.[vendor]));
    // One chat API is implied; an audio package never is, and an unpicked one routes the row
    // by its name, so `eleven-labs` reached openai-compatible instead of ElevenLabs.
    if (entries.length < 2 && !audio.length) return null;
    return (
      <select value={cellText(value, 'string')} disabled={disabled} title={title}
        onChange={(e) => onChange(e.target.value, 'string')}
        className="w-64 shrink-0 rounded border border-field-border bg-field px-2 py-1 text-xs text-fg focus:border-orange">
        <option value="">—</option>
        {entries.length > 0 && (
          <optgroup label="Chat APIs">
            {entries.map((e) => <option key={e.value} value={e.value}>{e.label}</option>)}
          </optgroup>
        )}
        {audio.length > 0 && (
          <optgroup label="Audio (transcription, speech, live)">
            {audio.map((e) => <option key={e.value} value={e.value}>{e.label}</option>)}
          </optgroup>
        )}
      </select>
    );
  }

  if (opts && col.searchable) {
    // Only what this row declares. A host's name is free text an admin may type
    // anything into, so it is not evidence about which vendor this is.
    const declared = col.field === 'catalog_namespace' ? cellText(value, 'string') : null;
    // Off, models.dev publishes nothing here, which says nothing about the vendor.
    const adrift = declared && dynamicOptions.models_dev !== false && !(opts || []).includes(declared);
    return (
      <div className="flex items-center gap-1">
        <SearchSelect value={cellText(value, 'string')} options={opts} disabled={disabled}
          title={title} onChange={(v) => onChange(v, 'string')} />
        {adrift && (
          <Hint icon={AlertTriangle} tone="text-amber-400"
            text={`models.dev no longer publishes "${declared}", so this host reaches no prices, limits or SDK binding and its models resolve to $0. Pick the vendor it is now.`} />
        )}
      </div>
    );
  }
  if (opts) {
    // Keep an unrecognised value selectable so it is not rewritten to opts[0].
    const list = opts.includes(value) ? opts : [value, ...opts].filter(Boolean);
    return (
      <select value={cellText(value, 'string')} disabled={disabled} title={title}
        onChange={(e) => onChange(e.target.value, 'string')}
        className="px-2 py-1 bg-field border border-field-border rounded text-fg text-xs focus:border-orange disabled:opacity-40">
        <option value="">—</option>
        {list.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    );
  }
  return (
    <input type={col.type === 'int' || col.type === 'float' ? 'number' : 'text'}
      step={col.type === 'float' ? '0.0001' : '1'} disabled={disabled} title={title}
      value={cellText(value, col.type)} placeholder={col.secret ? '••••' : ''}
      onChange={(e) => onChange(e.target.value, col.type)}
      className={`px-2 py-1 bg-field border border-field-border rounded text-fg text-xs focus:border-orange disabled:opacity-40 ${col.type === 'string' || col.type === 'json' ? 'w-56' : 'w-24'}`} />
  );
}

// Not the native `title`: it waits a second, cannot be styled and truncates.
// Fixed to the viewport because the group's table scrolls and clips absolutes.
function Hint({ text, icon: Glyph = Info, tone = 'text-fg-muted/70' }) {
  const [box, setBox] = useState(null);
  const icon = useRef(null);
  const place = () => {
    const r = icon.current?.getBoundingClientRect();
    if (r) setBox({ left: Math.min(r.left, window.innerWidth - 340), top: r.bottom + 6 });
  };
  return (
    <>
      {/* The icon is 12px; padding gives a ~28px target while `-m-2` keeps the
          layout identical, and the title makes it reachable when the pointer
          never lands at all. */}
      <span ref={icon} onMouseEnter={place} onMouseLeave={() => setBox(null)}
        onFocus={place} onBlur={() => setBox(null)} tabIndex={0} title={text}
        className="-m-2 inline-flex cursor-help p-2">
        <Glyph className={`w-3 h-3 shrink-0 ${tone}`} aria-hidden="true" />
      </span>
      {box && (
        <span style={{ left: box.left, top: box.top, maxWidth: 320 }}
          className="fixed z-50 rounded border border-field-border bg-panel px-3 py-2 text-[11px] font-normal normal-case leading-relaxed text-fg shadow-lg">
          {text}
        </span>
      )}
    </>
  );
}

function ColumnHeader({ col }) {
  if (!col.help) return col.label;
  return (
    <span className="inline-flex items-center gap-1">
      {col.label}
      <Hint text={col.help} />
    </span>
  );
}

function SectionHead({ children, help, right = null }) {
  return (
    <div className="mb-2 flex items-center justify-between gap-2">
      <span className="inline-flex items-center gap-1 text-xs font-medium text-fg">
        {children}
        {help && <Hint text={help} />}
      </span>
      {right}
    </div>
  );
}

function toText(value, type) {
  if (value === null || value === undefined) return '';
  if (type === 'bool') return value ? 'true' : 'false';
  return String(value);
}
function parseScalar(text, type) {
  if (type === 'bool') return ['1', 'true', 'yes', 'on'].includes(String(text).toLowerCase());
  if (type === 'int') { const n = parseInt(text, 10); if (Number.isNaN(n)) throw new Error('Not an integer'); return n; }
  if (type === 'float') { const n = parseFloat(text); if (Number.isNaN(n)) throw new Error('Not a number'); return n; }
  return text;
}

// Install-wide, so only a platform admin may turn it; everyone else sees where prices come from.
function ModelsDevSwitch({ options, onToggled }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const platformAdmin = localStorage.getItem('admin_is_admin') === 'true';
  const enabled = options.models_dev !== false;
  const forcedOff = !!options.models_dev_forced_off;

  const turn = async () => {
    setBusy(true); setError(null);
    try {
      await setModelsDev(!enabled);
      await onToggled(`models.dev ${enabled ? 'off' : 'on'}`);
    } catch (e) { setError(e.response?.data?.detail || e.message || 'Save failed'); } finally { setBusy(false); }
  };

  const locked = forcedOff || !platformAdmin || busy;
  const why = forcedOff ? 'Turned off by this deployment.'
    : !platformAdmin ? 'Only a platform admin can change this.' : null;
  return (
    <div className={`p-3 rounded-lg border ${enabled ? 'border-panel-border bg-surface' : 'border-amber-500/30 bg-amber-500/5'}`}>
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          <span className="text-fg text-sm font-medium">models.dev</span>
          <p className="text-fg-muted text-xs mt-1">
            {enabled
              ? 'Model configuration is inferred from models.dev.'
              : 'Model configuration is not inferred \u2014 configure models manually.'}
          </p>
          {why && <p className="text-fg-muted text-xs mt-1">{why}</p>}
          {error && <p className="mt-1 text-xs text-red-400 flex items-center gap-1"><AlertTriangle className="w-3 h-3" />{error}</p>}
        </div>
        <button type="button" role="switch" aria-checked={enabled} aria-label="models.dev"
          disabled={locked} title={why || undefined} onClick={turn}
          className="flex shrink-0 items-center gap-2 group disabled:cursor-not-allowed disabled:opacity-60">
          <span className={`relative w-8 h-[19px] rounded-full border shrink-0 transition-colors ${enabled ? 'bg-orange border-orange' : 'bg-field border-field-border group-hover:border-orange/50'}`}>
            <span className={`absolute top-[1.5px] left-[1.5px] w-3.5 h-3.5 rounded-full transition-transform ${enabled ? 'translate-x-[13px] bg-white' : 'bg-fg-muted'}`} />
          </span>
          <span className="text-xs font-medium text-fg">{busy ? 'Saving…' : enabled ? 'On' : 'Off'}</span>
        </button>
      </div>
    </div>
  );
}

function ScalarRow({ s, onChanged }) {
  const base = s.is_overridden ? s.current_value : s.default;
  const [text, setText] = useState(toText(base, s.value_type));
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const b = s.is_overridden ? s.current_value : s.default;
    setText(toText(b, s.value_type)); setDirty(false); setError(null);
  }, [s.is_overridden, s.current_value, s.default, s.value_type]);

  const save = async () => {
    setBusy(true); setError(null);
    try {
      await upsertSetting(s.key, parseScalar(text, s.value_type), { valueType: s.value_type, category: s.category });
      onChanged(`${s.key} saved`);
    } catch (e) { setError(e.response?.data?.detail || e.message || 'Save failed'); } finally { setBusy(false); }
  };
  const reset = async () => {
    setBusy(true); setError(null);
    try { await deleteSetting(s.key); onChanged(`${s.key} reset`); }
    catch (e) { setError(e.response?.data?.detail || e.message || 'Reset failed'); } finally { setBusy(false); }
  };

  return (
    <div className={`p-3 rounded-lg border ${dirty ? 'border-orange/40 bg-orange/5' : s.is_overridden ? 'border-blue-500/20 bg-blue-500/5' : 'border-panel-border bg-surface'}`}>
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-fg text-sm font-medium">{s.label}</span>
            {s.is_overridden && <span className="px-1.5 py-0.5 bg-blue-500/20 text-blue-400 text-xs rounded">overridden</span>}
            <span className="text-fg-muted text-xs font-mono">{s.key}</span>
          </div>
          <p className="text-fg-muted text-xs mt-1">{s.description}</p>
          <div className="text-xs text-fg-muted mt-1">Default: <span className="text-fg-muted">{s.value_type === 'bool' ? (s.default ? 'On' : 'Off') : String(s.default)}</span></div>
          {error && <p className="mt-1 text-xs text-red-400 flex items-center gap-1"><AlertTriangle className="w-3 h-3" />{error}</p>}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {s.value_type === 'bool' ? (
            <select value={String(text).toLowerCase() === 'true' ? 'true' : 'false'} onChange={(e) => { setText(e.target.value); setDirty(true); }}
              className="px-3 py-1.5 bg-field border border-field-border rounded text-fg text-sm focus:border-orange">
              <option value="true">On</option><option value="false">Off</option>
            </select>
          ) : (
            <input type={s.value_type === 'int' || s.value_type === 'float' ? 'number' : 'text'} step={s.value_type === 'float' ? '0.01' : '1'}
              value={text} onChange={(e) => { setText(e.target.value); setDirty(true); }}
              className="w-44 px-3 py-1.5 bg-field border border-field-border rounded text-fg text-sm focus:border-orange" />
          )}
          {dirty && <button onClick={save} disabled={busy} className="flex items-center gap-1 px-3 py-1.5 bg-orange hover:bg-orange-hover text-fg rounded text-xs disabled:opacity-50"><Save className="w-3 h-3" />Save</button>}
          {s.is_overridden && !dirty && <button onClick={reset} disabled={busy} title="Reset to default" className="p-1.5 text-fg-muted hover:text-fg rounded disabled:opacity-50"><RotateCcw className="w-3.5 h-3.5" /></button>}
        </div>
      </div>
    </div>
  );
}

// Chars shown before folding; the rest is one click away, never discarded.
const FOLD = 700;

// Absent and empty are different findings: no block at all is "—", a block with
// empty text is what `display: omitted` looks like.
function Said({ label, value, tone = 'text-fg' }) {
  // Per-field, so opening one reply does not close another.
  const [open, setOpen] = useState(false);

  if (value === null || value === undefined || value === '') {
    return (
      <p className="mt-1 flex gap-2 text-[11px] leading-relaxed">
        <span className="w-20 shrink-0 font-sans text-fg-muted">{label}</span>
        {value === ''
          ? <span className="italic text-fg-muted">(empty — the provider returned no text)</span>
          : <span className="text-fg-muted">—</span>}
      </p>
    );
  }

  const folded = value.length > FOLD;
  return (
    <div className="mt-1 flex gap-2 text-[11px] leading-relaxed">
      <span className="w-20 shrink-0 font-sans text-fg-muted">{label}</span>
      <div className="min-w-0 flex-1">
        {/* `anywhere`, not `break-words`: a base64 signature has no break
            opportunity, so the cell grew and took the table off the right edge. */}
        <div className={`whitespace-pre-wrap [overflow-wrap:anywhere] ${tone}`}>
          {folded && !open ? `${value.slice(0, FOLD)}…` : value}
        </div>
        {/* Own line: trailing the text it read as part of the reply. */}
        {folded && (
          <button type="button" onClick={() => setOpen(!open)}
            className="mt-1 rounded border border-field-border bg-field px-2 py-0.5 font-sans
                       text-[10px] text-fg-muted hover:border-accent hover:text-fg">
            {open ? 'Show less' : `Show more · ${value.length - FOLD} more`}
          </button>
        )}
      </div>
    </div>
  );
}

// Measured over models.dev: of 1,013 models served by more than one provider,
// cost disagrees on 78%, limits 65%, reasoning controls 59%. Host is an axis.

// `missing` sorts first: it only appears on a host models.dev cannot place, which
// nothing here is priced or limited by the catalog until the row says so.
const STATE_ORDER = { missing: -1, conflict: 0, ours: 1, inferred: 2, local: 3, agrees: 4, unset: 5 };
// `unset`: the row stores nothing and the catalog answers (saving would pin it). `ours`:
// resolved from `defaults.py`, stated by neither models.dev nor the admin. `inferred`: read off
// the catalog's ladder, which states no such field.
// What an absence costs, for the fields nothing else answers for. Figures never appear
// here — `in_force` carries what actually resolves.
const UNSTATED_HINT = {
  cache_read: 'cached reads bill at 0 until set',
  cache_write: 'cache writes bill at 0 until set',
  tiers: 'no context bands — the flat rate bills at every size',
};

// The router states which fields refuse the save and what answers otherwise.
// `missing` is a host models.dev cannot place, so a value in force there is the default config's.
const unstatedHint = (f) =>
  (f.blocks ? 'required' :
    f.in_force != null && f.state === 'missing' ? `using default config — ${f.in_force}` :
      f.in_force != null ? `unset — resolves to ${f.in_force}` :
        UNSTATED_HINT[f.path] || 'not stated');

// Where an inherited figure came from. Only models.dev's own answer renders at full
// brightness; anything else is this deployment's and says so.
const INHERITED_FROM = {
  unset: 'From models.dev for this host',
  agrees: 'From models.dev — your row states the same',
  local: "Inherited from this model's row — you set this, not models.dev",
  ours: 'Measured by this project, not models.dev',
  missing: 'models.dev has no entry for this id — set it here',
};

// Named beside each field: `audio_minute` is per minute while a provider may quote per hour,
// and `characters` per million, so an unlabelled box took a price in the wrong unit.
const FIELD_UNITS = {
  input: '$/M tokens', output: '$/M tokens', cache_read: '$/M tokens', cache_write: '$/M tokens',
  input_audio: '$/M tokens', output_audio: '$/M tokens', audio_minute: '$/min',
  characters: '$/M chars', audio_minimum_seconds: 'seconds', context_window: 'tokens',
  max_output_tokens: 'tokens', rpm: 'requests/min',
};

// The units providers quote that the router stores in another: an hour is ÷ 60 of `audio_minute`,
// a thousand characters × 1000 of `characters`. Entered on their own row, never stored.
const QUOTED_AS = {
  audio_minute: { name: 'audio_hour', unit: '$/hour', factor: 1 / 60 },
  characters: { name: 'characters_1k', unit: '$/1K chars', factor: 1000 },
};

const NUMERIC_FIELDS = new Set(['input', 'output', 'cache_read', 'cache_write',
                                'input_audio', 'output_audio', 'audio_minute', 'characters',
                                'audio_minimum_seconds', 'context_window', 'max_output_tokens',
                                'rpm']);

// One rule for both inputs. `JSON.parse` throws on `.5`, `+5` and `0123`, and the string
// it leaves makes `asText` true, so the field never recovers. `null` stays a string:
// `_layered` reads one as an erasure.
const parseField = (path, raw, asText = false) => {
  // Blank, spaces included: `Number(' ')` is 0, and a stated 0 saved a required price as free.
  if (raw.trim() === '') return '';
  if (NUMERIC_FIELDS.has(path)) {
    const n = Number(raw);
    return Number.isFinite(n) ? n : raw;
  }
  if (asText) return raw;
  try { return JSON.parse(raw); } catch { return raw; }
};

const STATE_LABEL = {
  missing: 'not stated', conflict: 'conflict', ours: 'measured here', inferred: 'inferred',
  local: 'yours only', agrees: 'matches', unset: 'from models.dev',
};
const STATE_TONE = {
  missing: 'text-red-400', conflict: 'text-amber-400', ours: 'text-orange', inferred: 'text-fg-muted',
  local: 'text-fg-muted', agrees: 'text-green-400', unset: 'text-sky-400',
};

const show = (v) => v === undefined ? '—' : typeof v === 'string' ? v : JSON.stringify(v);

// Never writes the shared row: a fix made on one host must not move the others.
export function writeHostField(row, host, path, value) {
  const [field, key] = path.split('.');
  const per = { ...(row.per_provider || {}) };
  const patch = { ...(per[host] || {}) };
  // Blank is "not stated": written as '', a cleared price was a string in a numeric field.
  if (key && value === '') {
    const { [key]: _gone, ...rest } = patch[field] || {};
    patch[field] = rest;
  } else if (key) patch[field] = { ...(patch[field] || {}), [key]: value };
  else if (value === '') delete patch[field];
  else patch[field] = value;
  per[host] = patch;
  return { ...row, per_provider: per };
}

// Every field on one host, not only the disagreements: a value filled from the
// catalog is still one nobody has read.
export function FetchReview({ host, view, onApply, onClose }) {
  const fields = view.fields || {};
  // Defaults: conflict keeps yours, anything else stays live so an untouched
  // field keeps tracking models.dev rather than freezing today's value.
  const [take, setTake] = useState({});
  const [edited, setEdited] = useState({});
  // What was typed, shown as typed: `edited` holds the parsed number, and a number cannot hold
  // "2." — rendered back, the point vanished, so "2.5" saved as 25 and "0.05" as 5 (review #335).
  const [typing, setTyping] = useState({});
  const typed = (path, raw, asText = false) => {
    // Typing the stored rate retires its quoted-unit draft, which would otherwise show a stale figure.
    setTyping(({ [QUOTED_AS[path]?.name ?? path]: _stale, ...t }) => ({ ...t, [path]: raw }));
    setEdited((p) => ({ ...p, [path]: parseField(path, raw, asText) }));
  };
  const settled = (path) => setTyping(({ [path]: _gone, ...rest }) => rest);

  const rows = useMemo(() => Object.entries(fields).sort(
    ([a, x], [b, y]) => (STATE_ORDER[x.state] - STATE_ORDER[y.state]) || a.localeCompare(b)),
    [fields]);
  const conflicts = rows.filter(([, f]) => f.state === 'conflict');
  // The router says why, what it costs and how bad it is; deriving any of it here got
  // `state` wrong and coloured an informational note like a fatal one.
  const why = view.problems || [];

  const chosen = (path, f) => {
    if (path in edited) return { write: true, value: edited[path] };
    const mode = take[path] ?? (f.state === 'conflict' ? 'mine' : 'live');
    return mode === 'catalog' ? { write: true, value: f.catalog } : { write: false };
  };
  // A blank where nothing was stated changes nothing, so it is not counted as a field to save.
  const writes = rows.filter(([p, f]) => chosen(p, f).write
    && !(f.state === 'missing' && chosen(p, f).value === ''));

  const apply = () => onApply(writes.map(([p, f]) => [p, chosen(p, f).value]));

  // A render function, not a component: a component declared here remounts each keystroke.
  // Booleans are selects; `can_disable` stays three-valued, since unmeasured is not no.
  const kindOf = (path, f) => {
    if (path === 'reasoning.can_disable') return 'tristate';
    const sample = f.catalog !== undefined ? f.catalog : f.declared;
    if (typeof sample === 'boolean' || path.startsWith('supports_')) return 'bool';
    return 'free';
  };

  // One class for every control in the "value to save" column: a `select` and an
  // `input` do not share a default line-height, and the select was missing `font-mono`.
  const FIELD_CLASS = 'h-[22px] w-full rounded border border-field-border bg-field '
    + 'px-1.5 py-0.5 font-mono text-[11px] leading-[14px] text-fg focus:border-orange';

  const renderValue = (path, f) => {
    // On `unset` the field shows `f.catalog` and marks nothing for saving: pinning freezes it.
    const resolved = f.state === 'unset' ? f.catalog : f.declared;
    const live = path in edited ? edited[path] : (take[path] === 'catalog' ? f.catalog : resolved);
    const asText = typeof live === 'string';
    const kind = kindOf(path, f);

    if (kind === 'bool' || kind === 'tristate') {
      // Spelled as models.dev spells it, since its value sits in the next column.
      // `unmeasured` has no spelling there because the field is simply absent.
      const choices = kind === 'tristate'
        ? [['', 'unmeasured'], ['true', 'true'], ['false', 'false']]
        : [['true', 'true'], ['false', 'false']];
      const current = live === true ? 'true' : live === false ? 'false' : '';
      return (
        <select value={current}
          onChange={(e) => setEdited((prev) => ({ ...prev, [path]:
            e.target.value === '' ? null : e.target.value === 'true' }))}
          className={FIELD_CLASS}>
          {kind === 'bool' && current === '' && <option value="">—</option>}
          {choices.map(([v, label]) => <option key={v} value={v}>{label}</option>)}
        </select>
      );
    }

    if (!(path in edited) && f.state === 'missing') {
      return (
        <input value={typing[path] ?? ''} placeholder={unstatedHint({ ...f, path })} title={f.blocks}
          onBlur={() => settled(path)}
          // `tiers` is the one container here and `Number('[]')` is NaN, so it could only ever be
          // stored as a string and the save refused it. The scalars stay on `Number`, which takes
          // `.5` and `+5` where `JSON.parse` throws on both.
          onChange={(e) => typed(path, e.target.value)}
          className={`${FIELD_CLASS} placeholder:text-fg-muted/60 ${
            f.blocks ? 'border-red-500/40' : ''}`} />
      );
    }
    return (
      <input
        value={typing[path]
          ?? (path in edited || asText ? (asText ? live : JSON.stringify(live ?? null)) : show(live))}
        onBlur={() => settled(path)}
        onChange={(e) => typed(path, e.target.value, asText)}
        className={FIELD_CLASS} />
    );
  };


  const renderQuoted = (path, f, { name, unit, factor }) => {
    const stored = path in edited ? edited[path] : (f.state === 'unset' ? f.catalog : f.declared);
    const onQuoted = (raw) => {
      const n = Number(raw);
      const converted = raw.trim() === '' || !Number.isFinite(n) ? raw : String(Number((n * factor).toFixed(10)));
      setTyping((t) => ({ ...t, [name]: raw, [path]: converted }));
      setEdited((prev) => ({ ...prev, [path]: parseField(path, converted) }));
    };
    return (
      <input value={typing[name] ?? (typeof stored === 'number' ? asQuoted(stored, factor) : '')}
        placeholder={`${unit} — fills ${path}`} inputMode="decimal"
        onBlur={() => settled(name)} onChange={(e) => onQuoted(e.target.value)}
        className={`${FIELD_CLASS} placeholder:text-fg-muted/60`} />
    );
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-start justify-center overflow-y-auto bg-black/70 p-6" onClick={onClose}>
      <div className="my-auto w-full max-w-5xl rounded-xl border border-panel-border bg-panel p-5 shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="mb-3 flex items-start justify-between gap-4">
          <div>
            <h3 className="font-semibold text-fg">
              models.dev · <span className="font-mono text-sm">{host}</span>
            </h3>
            <p className="mt-1 text-xs text-fg-muted">
              {conflicts.length > 0
                ? <span className="text-amber-400">{conflicts.length} conflict{conflicts.length === 1 ? '' : 's'} — your value is kept unless you say otherwise.</span>
                : 'Nothing here disagrees with what you have set.'}
              <Hint text="Every field is listed, not only the disagreements: a value filled from the catalog is still one nobody has read." />
            </p>
          </div>
          <button onClick={onClose} className="p-1 text-fg-muted hover:text-fg"><X className="h-4 w-4" /></button>
        </div>

        {why.map((p) => (
          <p key={p.message}
            className={`mb-3 flex items-start gap-1 rounded border p-2 text-xs ${
              p.severity === 'blocking'
                ? 'border-red-500/50 bg-red-500/5 text-red-400'
                : 'border-amber-500/50 bg-amber-500/5 text-amber-400'}`}>
            <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" />
            <span>{p.message}</span>
          </p>
        ))}

        <div className="max-h-[26rem] overflow-y-auto rounded border border-panel-border">
          <table className="w-full table-fixed text-[11px]">
            <thead className="sticky top-0 bg-panel text-fg-muted">
              <tr className="border-b border-panel-border">
                <th className="w-24 px-2 py-1.5 text-left font-medium">state</th>
                <th className="w-56 px-2 py-1.5 text-left font-medium">field</th>
                <th className="px-2 py-1.5 text-left font-medium">value to save</th>
                <th className="w-40 px-2 py-1.5 text-left font-medium">models.dev</th>
                <th className="w-24 px-2 py-1.5 text-left font-medium" />
              </tr>
            </thead>
            <tbody>
              {rows.map(([path, f]) => {
                const write = chosen(path, f).write;
                return (
                  <React.Fragment key={path}>
                  <tr className={`border-b border-panel-border/40 ${write ? 'bg-orange/5' : ''}`}>
                    <td className={`px-2 py-1 ${f.state === 'missing' && !f.blocks
                      ? 'text-fg-muted' : STATE_TONE[f.state]}`}>
                      {STATE_LABEL[f.state]}
                      {f.state === 'inferred' && (
                        <span className="ml-1 cursor-help text-[10px] text-fg-muted"
                          title="models.dev has no such field. This is read off the levels it lists for this host: `none` or a toggle there is taken to mean an off switch. Test live or set it yourself; either replaces this, and neither is a conflict.">
                          why
                        </span>
                      )}
                      {f.state === 'unset' && (
                        <span className="ml-1 cursor-help text-[10px] text-fg-muted"
                          title="Live already — the router resolves this from models.dev. Your row stores nothing, and saving would pin today's value rather than keep tracking it.">
                          live
                        </span>
                      )}
                      {f.state === 'conflict' && f.scope === 'row' && (
                        <span className="ml-1 text-[10px] text-fg-muted" title="Set on the shared row, so it answers for every host. Taking the catalog value writes it to this host alone.">shared</span>
                      )}
                    </td>
                    <td className="break-all px-2 py-1 font-mono text-fg" title={path}>
                      {path}
                      {FIELD_UNITS[path] && (
                        <span className="ml-1 font-sans text-[10px] text-fg-muted">({FIELD_UNITS[path]})</span>
                      )}
                    </td>
                    <td className="px-2 py-1">{renderValue(path, f)}</td>
                    <td className="break-all px-2 py-1 font-mono text-fg-muted"
                      title={show(f.catalog)}>
                      {show(f.catalog)}
                    </td>
                    <td className="px-2 py-1">
                      {/* Only where taking it is a real choice. On `agrees` it is a
                          no-op; on `unset` the catalog already answers and the router
                          already uses it, so the button's one effect is to FREEZE
                          today's number — and on a first fill every field is `unset`,
                          which made the screen read as work to do. Pinning stays
                          reachable: type the value, or use Select all. */}
                      {f.catalog !== undefined
                        && f.state !== 'agrees' && f.state !== 'unset' && (
                        <button type="button"
                          onClick={() => { setEdited(({ [path]: _, ...r }) => r); setTake((p) => ({ ...p, [path]: write ? 'mine' : 'catalog' })); }}
                          className={`rounded border px-1.5 py-0.5 text-[10px] ${write
                            ? 'border-orange bg-orange/15 text-orange'
                            : 'border-panel-border text-fg-muted hover:text-fg'}`}>
                          {write ? <><Check className="mr-0.5 inline h-2.5 w-2.5" />saving</> : 'use theirs'}
                        </button>
                      )}
                    </td>
                  </tr>
                  {QUOTED_AS[path] && (
                    <tr className="border-b border-panel-border/40">
                      <td className="px-2 py-1 text-fg-muted">fills {FIELD_UNITS[path]}</td>
                      <td className="px-2 py-1 font-mono text-fg">
                        {QUOTED_AS[path].name}
                        <span className="ml-1 font-sans text-[10px] text-fg-muted">({QUOTED_AS[path].unit})</span>
                      </td>
                      <td className="px-2 py-1">{renderQuoted(path, f, QUOTED_AS[path])}</td>
                      <td className="px-2 py-1 font-mono text-fg-muted">—</td>
                      <td />
                    </tr>
                  )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className="mt-3 flex items-center gap-2 border-t border-panel-border pt-3">
          <button onClick={apply} disabled={!writes.length}
            className="flex items-center gap-1 rounded bg-orange px-4 py-1.5 text-sm text-fg hover:bg-orange-hover disabled:opacity-40">
            <Save className="h-4 w-4" />Save {writes.length} field{writes.length === 1 ? '' : 's'}
          </button>
          <button type="button"
            onClick={() => setTake(writes.length
              ? {}
              : Object.fromEntries(rows.filter(([, f]) => f.catalog !== undefined).map(([p]) => [p, 'catalog'])))}
            title={writes.length
              ? 'Deselect everything'
              : "Selects every value models.dev states. Saving them freezes today's answer onto this host — right for pricing, a maintenance cost everywhere else."}
            className="rounded border border-panel-border px-3 py-1.5 text-sm text-fg-muted hover:text-fg">
            {writes.length ? 'Select none' : 'Select all'}
          </button>
          <button onClick={onClose} className="rounded border border-panel-border px-3 py-1.5 text-sm text-fg-muted hover:text-fg">Cancel</button>
          <span className="inline-flex items-center gap-1 text-xs text-fg-muted">
            Untouched fields stay live
            <Hint text="They keep tracking models.dev. Saving a value freezes today's answer onto this host until someone changes it." />
          </span>
        </div>
      </div>
    </div>
  );
}

function Tri({ label, hint, value, onChange, locked }) {
  const choices = [[null, 'unmeasured'], [true, 'yes'], [false, 'no']];
  return (
    <div>
      <div className="mb-1 flex items-center gap-1 text-[11px] text-fg-muted">
        {label}
        {hint && <Hint text={hint} />}
      </div>
      <div className="flex gap-1">
        {choices.map(([v, text]) => (
          <button key={text} type="button" disabled={locked}
            onClick={() => onChange(v)}
            title={locked ? 'Fixed by the shape above' : undefined}
            className={`rounded border px-2 py-0.5 text-[11px] ${
              value === v ? 'border-orange bg-orange/15 text-orange'
                : 'border-panel-border text-fg-muted hover:text-fg'} ${
              locked ? 'cursor-not-allowed opacity-40' : ''}`}>
            {text}
          </button>
        ))}
      </div>
    </div>
  );
}

// The row's own modality decides the call: an audio model does one kind of thing.
const AUDIO_KINDS = { transcription: 'transcription', speech: 'speech', realtime: 'live' };

// Whose count the bill is. `provider` is the host's own; the rest are our measure of a host
// that reports none, and are only as right as the price row they are multiplied by.
const METERED = {
  provider: 'provider-reported', duration: 'audio length', characters: 'characters sent',
  clock: 'session time', measured: 'clip length, measured by us', estimate: 'estimated',
};

function AudioSection({ row, model, provider, kind }) {
  const [test, setTest] = useState(null);      // null · 'running' · report
  const [open, setOpen] = useState(false);

  const runTest = () => {
    setTest('running');
    setOpen(false);
    // Same as the reasoning run: a rejection still spent money, so render what came back.
    Promise.allSettled([testAudio({ provider, model, kind, row })]).then(([res]) => {
      const report = res.status === 'fulfilled' ? res.value?.data ?? {} : {};
      const rejected = res.status === 'rejected'
        ? res.reason?.response?.data?.detail || res.reason?.message : null;
      setTest({ ...report, error: rejected || report.error || null });
    });
  };
  const r = test && test !== 'running' ? test.rows?.[0] : null;

  return (
    <div className="mt-3 rounded border border-panel-border bg-field/30 p-3">
      <SectionHead help={'One live call as this row\u2019s modality: a clip transcribed, a sentence spoken, or one turn of a live session. It answers whether this host serves the model, what it cost, and whether that cost is the provider\u2019s own count or our measure of it.'}>
        Audio
      </SectionHead>
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-[11px] text-fg-muted">
          as <span className="font-mono text-fg">{AUDIO_KINDS[kind]}</span>, this row's modality
        </span>
        <button onClick={runTest} disabled={test === 'running' || !provider}
          title="Calls the model once — spends money"
          className="flex items-center gap-1 rounded border border-panel-border bg-panel px-3 py-1.5 text-sm text-fg-muted hover:text-fg disabled:opacity-40">
          <FlaskConical className="h-4 w-4" />
          {test === 'running' ? 'Testing\u2026' : 'Test live'}
        </button>
      </div>

      {test && test !== 'running' && test.error && (
        <p className="mt-2 flex items-start gap-1 text-xs text-red-400">
          <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" />{test.error}
        </p>
      )}

      {r && (
        <div className="mt-2 rounded border border-panel-border bg-field/40 p-3 text-[11px]">
          <table className="w-full table-fixed">
            <thead className="text-fg-muted"><tr>
              {['http', 'result', 'billed by', 'cost'].map((h) => (
                <th key={h} className="pb-1 text-left font-medium">{h}</th>))}
            </tr></thead>
            <tbody className="font-mono text-fg">
              <tr onClick={() => setOpen(!open)} className="cursor-pointer hover:bg-field">
                <td className={r.http === 200 ? 'text-green-400' : 'text-red-400'}>
                  <ChevronRight className={`mr-1 inline h-3 w-3 transition-transform ${open ? 'rotate-90' : ''}`} />
                  {r.http ?? '\u2014'}
                </td>
                <td className="truncate" title={r.detail || ''}>
                  {r.heard != null ? `heard ${Math.round(r.heard * 100)}% \u00b7 ` : ''}{r.detail || '\u2014'}
                </td>
                <td>{METERED[r.metered_by] || '\u2014'}</td>
                <td>${(r.cost || 0).toFixed(6)}</td>
              </tr>
              {r.metered_by && r.metered_by !== 'provider' && (
                <tr><td colSpan={4} className="pt-1 font-sans text-amber-400">
                  {'\u21b3 the host reported no usage, so this is our measure times this row\u2019s price'}
                </td></tr>
              )}
              {r.http === 200 && !r.cost && (
                <tr><td colSpan={4} className="pt-1 font-sans text-amber-400">
                  {'\u21b3 billed at $0 \u2014 no price is set for this model, so budgets cannot see its spend'}
                </td></tr>
              )}
              {open && (
                <tr><td colSpan={4} className="pt-2">
                  <Said label="usage" value={r.raw ? JSON.stringify(r.raw, null, 2) : null} tone="text-fg-muted" />
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// Capability is the catalog's, policy the org's and only subtracts; `narrow()` keeps "has no
// max" apart from "chose not to pay for max".
function ReasoningSection({ row, model, provider, hostViews, onRow }) {
  const modelsDev = useContext(ModelsDevContext);
  const spec = row.reasoning || {};
  const [test, setTest] = useState(null);      // null · 'running' · result
  // Empty means every level; a sweep costs one call per level.
  const [testLevels, setTestLevels] = useState([]);
  // A set, not one level: an accordion closes the row you were comparing against.
  const [openLevels, setOpenLevels] = useState(() => new Set());
  const toggleLevel = (level) => setOpenLevels((prev) => {
    const next = new Set(prev);
    if (!next.delete(level)) next.add(level);
    return next;
  });

  // The host this section describes and tests: hosts differ, so testing one while showing
  // another's ladder would report the wrong measurement.
  const servingHosts = Object.entries(row.host_id || {})
    .filter(([, id]) => String(id || '').trim()).map(([host]) => host);
  const [picked, setPicked] = useState('');
  const host = servingHosts.includes(picked) ? picked : provider;

  // Prefer the host under test; any host that answers will do to show what the
  // catalog knows.
  const view = hostViews?.[host] || Object.values(hostViews || {}).find((v) => v?.capabilities);
  const derived = (view?.capabilities?.reasoning) || {};
  // Three answers, not two: it reasons, it does not, or nothing was said. Folding
  // the middle into the last claimed silence about a model the catalog describes.
  const catalogSaidSomething = !!derived.mode;
  // `reasoning_capability`: a declaration wins and `supports_reasoning` answers when
  // there is none, so a row carrying only the flag reasons off the router's ladder.
  const assumed = !catalogSaidSomething && view?.capabilities?.supports_reasoning === true;
  const reasons = catalogSaidSomething ? derived.mode !== 'none' : assumed;
  const ladder = derived.effort || [];

  const allowed = Array.isArray(row.allowed_effort) ? row.allowed_effort : [];
  // Empty is unrestricted, so a first click subtracts from the whole ladder rather
  // than builds up — and striking every level would read as unrestricted too.
  const toggleAllowed = (tier) => {
    const from = allowed.length ? allowed : ladder;
    const next = ladder.filter(
      (t) => (t === tier ? !from.includes(t) : from.includes(t)));
    if (!next.length) return;
    // `onRow` directly, not `setRow`: that merges over `row`, so a stripped key
    // survives and un-withholding the last level was a no-op. Same trap as `__replace`.
    onRow(next.length === ladder.length
      ? (({ allowed_effort, ...rest }) => rest)(row)
      : { ...row, allowed_effort: next });
  };

  const offered = allowed.length ? allowed : ladder;

  // The resolved answer, not the stored row: a catalog-sourced ladder has no row block, and
  // reading the row hid the `off` chip the hosts table showed.
  const offersOff = reasons && derived.can_disable !== false;
  // What a trial sweeps, off the resolved per-host ladder narrowed by `allowed_effort`,
  // as `reasoning_spec()` narrows it. Never `row.reasoning.effort`: `resolve()` already
  // folds that in, and reading it again undercounted a host whose ladder is wider —
  // the chips lost `max` while the sweep still ran it.
  const sweepLevels = allowed.length ? ladder.filter((l) => allowed.includes(l)) : ladder;
  const testable = [...sweepLevels, ...(offersOff ? ['off'] : [])];
  const toggleTestLevel = (name) => setTestLevels((prev) =>
    prev.includes(name) ? prev.filter((l) => l !== name) : [...prev, name]);
  // One per chip, `structured` included — it is two live calls underneath, but the
  // number has to match what is on screen or it reads as a miscount.
  const plannedLevels = testLevels.length || (sweepLevels.length ? testable.length : 0);

  // Unset, the host picks the depth, and models.dev states no default. `high` is on 97.2% of
  // 3,222 catalog ladders; applied only when the row is silent (`""` counts as answered).
  useEffect(() => {
    if (!reasons || !offered.length) return;
    if (spec.default !== undefined) return;
    onRow({ ...row, reasoning: { ...spec, default: preferredDefault(offered) } });
  }, [reasons, offered.join(','), spec.default]);

  // Written into the document only (Apply persists), from the row as it is now: a run takes
  // minutes, and a render-time closure reverted every edit made meanwhile.
  const rowRef = useRef(row);
  useEffect(() => { rowRef.current = row; });

  const applyMeasured = (...patches) => {
    const next = withMeasured(rowRef.current, host, ...patches);
    if (next) onRow(next);
  };

  const runTest = () => {
    setTest('running');
    setOpenLevels(new Set());
    // `allSettled`, not a bare catch: the run spends real tokens, so a rejection has
    // to render what came back rather than discard it.
    Promise.allSettled([testReasoning({ provider: host, model, row, levels: testLevels })])
      .then(([res]) => {
        const report = res.status === 'fulfilled' ? res.value?.data ?? {} : {};
        const rejected = res.status === 'rejected'
          ? res.reason?.response?.data?.detail || res.reason?.message : null;
        setTest({ ...report, error: rejected || (report.ok === false ? report.error : null) });
        if (report.ok !== false) applyMeasured(report.measured);
      });
  };

  return (
    <div className="mt-3 rounded border border-panel-border bg-field/30 p-3">
      <SectionHead help={`${modelsDev ? 'What the model accepts is the catalog\u2019s answer and is shown here rather than asked for. ' : ''}What this organisation lets callers reach is yours, and can only ever subtract from it.`}>
        Reasoning
      </SectionHead>

      {catalogSaidSomething && !reasons && (
        <p className="text-[11px] text-fg-muted">
          {modelsDev ? 'models.dev says this model' : 'This model'} does not reason on{' '}
          <span className="font-mono">{provider || 'this host'}</span>, so there is no
          ladder to offer and nothing to withhold.
        </p>
      )}

      {reasons ? (
        <div className="mb-3 space-y-1 text-[11px]">
          {/* Only what the chips below are read against. The off switch and the
              per-host comparison are the hosts table's job, directly above. */}
          {[['shape', derived.mode],
            ['levels', ladder.length ? ladder.join(', ') : '\u2014']].map(([k, v]) => (
            <div key={k} className="flex gap-2">
              <span className="w-24 shrink-0 text-fg-muted">{k}</span>
              <span className="font-mono text-fg">{v}</span>
            </div>
          ))}
        </div>
      ) : !catalogSaidSomething && (
        <p className={`mb-3 text-[11px] ${modelsDev ? 'text-amber-400' : 'text-fg-muted'}`}>
          {assumed
            ? 'Nothing states a ladder for this host, and this model is marked as reasoning \u2014 so the router falls back to its own conservative one. Declare the ladder to decide it here instead.'
            : `${modelsDev ? 'The catalog states nothing' : 'Nothing states a ladder'} for this host, so the declaration below is the only thing that decides what callers may ask for.`}
        </p>
      )}

      {ladder.length > 0 && (
        <div className="mb-3">
          <div className="mb-1 text-[11px] text-fg-muted">
            Levels callers here may ask for — empty means every level above
          </div>
          <div className="flex flex-wrap gap-1">
            {ladder.map((t) => (
              <button key={t} type="button" onClick={() => toggleAllowed(t)}
                className={`rounded border px-2 py-0.5 font-mono text-[11px] ${
                  offered.includes(t) ? 'border-orange bg-orange/15 text-orange'
                    : 'border-panel-border text-fg-muted line-through hover:text-fg'}`}>
                {t}
              </button>
            ))}
          </div>
          {allowed.length > 0 && (
            <p className="mt-1 text-[11px] text-fg-muted">
              Policy, not capability: the model still accepts the struck-through levels —
              this organisation does not offer them.
            </p>
          )}
        </div>
      )}

      {offered.length > 0 && (
        <div className="mb-1">
          <div className="mb-1 flex items-center gap-1 text-[11px] text-fg-muted">
            Default effort
            <Hint text={modelsDev
              ? 'What a caller gets when it asks to think but names no depth. models.dev states this for no model on any host — measured across every catalogued pair — so it is the one part of the ladder that has to be chosen rather than read.'
              : 'What a caller gets when it asks to think but names no depth.'} />
          </div>
          <select value={spec.default ?? ''}
            onChange={(e) => onRow({ ...row,
              reasoning: { ...spec, default: e.target.value } })}
            className="rounded border border-field-border bg-field px-2 py-1 text-[11px] text-fg">
            <option value="">— let the host decide —</option>
            {offered.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
      )}

        {/* Gated on the same rule `verify.check` refuses by: a row that declares no
            reasoning had nothing to test and the button answered that refusal instead
            of a run. The section renders only once the row names an id for its host. */}
      {reasons && (
      <div className="mt-3 border-t border-panel-border pt-3">
        <div className="flex flex-wrap items-center gap-2">
          <button onClick={runTest} disabled={test === 'running' || !host}
            title="Calls the model at each level — spends tokens"
            className="flex items-center gap-1 rounded border border-panel-border bg-panel px-3 py-1.5 text-sm text-fg-muted hover:text-fg disabled:opacity-40">
            <FlaskConical className="h-4 w-4" />
            {test === 'running' ? 'Testing…'
              : (() => {
                  // No ladder anywhere means the router picks one; naming a
                  // number here would be inventing it.
                  return plannedLevels
                    ? `Test live · ${plannedLevels} level${plannedLevels === 1 ? '' : 's'}`
                    : 'Test live';
                })()}
          </button>
          {/* Which host is under test. Only when the row names more than one — with a
              single host the label already says it, and a select with one option reads
              like a decision nobody has to make. */}
          {servingHosts.length > 1 && (
            <select value={host} onChange={(e) => setPicked(e.target.value)}
              title="Hosts differ: the ladder, the off switch and the price are per host, and so is what a run measures"
              className="rounded border border-panel-border bg-field px-1.5 py-0.5 font-mono text-[11px] text-fg">
              {servingHosts.map((name) => (
                <option key={name} value={name}>
                  {name}{name === provider ? ' · served as' : ''}
                </option>
              ))}
            </select>
          )}
          {/* Every level costs a call. None ticked means all of them. */}
          {testable.length > 1 && (
            <span className="flex items-center gap-1">
              {testable.map((name) => (
                <button key={name} type="button" onClick={() => toggleTestLevel(name)}
                  title={testLevels.length ? undefined : 'No selection tests every level'}
                  className={`rounded border px-1.5 py-0.5 font-mono text-[11px] ${
                    testLevels.includes(name)
                      ? 'border-orange bg-orange/15 text-orange'
                      : 'border-panel-border text-fg-muted hover:text-fg'}`}>
                  {name}
                </button>
              ))}
              {testLevels.length > 0 && (
                <button type="button" onClick={() => setTestLevels([])}
                  className="ml-0.5 text-[11px] text-fg-muted underline hover:text-fg">all</button>
              )}
            </span>
          )}
        </div>

        {test && test !== 'running' && test.error && (
          <p className="mt-2 flex items-start gap-1 text-xs text-red-400">
            <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" />{test.error}
          </p>
        )}

        {test && test !== 'running' && test.rows?.length > 0 && (
          <div className="mt-2 rounded border border-panel-border bg-field/40 p-3">
            {test.rows?.length > 0 && (
            <table className="w-full table-fixed text-[11px]">
              <thead className="text-fg-muted"><tr>
                {['level', 'http', 'thinking', 'signature', 'tokens', 'cost'].map((h) => (
                  <th key={h} className="pb-1 text-left font-medium">{h}</th>))}
              </tr></thead>
              <tbody className="font-mono text-fg">
                {test.rows.map((r) => (
                  <React.Fragment key={r.level}>
                    <tr onClick={() => toggleLevel(r.level)}
                      className="cursor-pointer hover:bg-field">
                      <td className="py-0.5">
                        <ChevronRight className={`mr-1 inline h-3 w-3 transition-transform ${openLevels.has(r.level) ? 'rotate-90' : ''}`} />
                        {r.level}
                      </td>
                      <td className={r.http === 200 ? 'text-green-400' : 'text-red-400'}>{r.http ?? '—'}</td>
                      <td>{r.thinking === null ? '—' : r.thinking ? 'yes' : 'none'}</td>
                      <td>{r.signature === null ? '—' : r.signature ? 'yes' : 'no'}</td>
                      <td>{r.tokens}</td>
                      <td>${r.cost.toFixed(5)}</td>
                    </tr>
                    {r.error && <tr><td colSpan={6} className="pb-1 pl-3 font-sans text-[11px] text-red-400">↳ {r.error}</td></tr>}
                    {r.honoured === false && (
                      <tr><td colSpan={6} className="pb-1 pl-3 font-sans text-[11px] text-amber-400">
                        ↳ accepted the disable and thought anyway — you are billed for what you switched off
                      </td></tr>
                    )}
                    {openLevels.has(r.level) && (
                      <tr><td colSpan={6} className="pb-2 pl-4">
                        {/* The whole payload: picked-out fields only re-rendered
                            text already in it, under `reasoning_details`. */}
                        <Said label="raw" value={r.raw ? JSON.stringify(r.raw, null, 2) : null} tone="text-fg-muted" />
                      </td></tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
            )}
            <p className="mt-2 text-[11px] text-fg-muted">
              Total ${test.total_cost} · click a level to see what came back
            </p>
          </div>
        )}
      </div>
      )}

    </div>
  );
}

/** Typed entry for a reasoning spec. The raw document stays authoritative — this
 *  writes into it — so Advanced and this view can never disagree. */
function ReasoningForm({ spec, onChange }) {
  const current = spec || {};
  const mode = current.mode || 'none';
  const shape = SHAPES.find((s) => s.mode === mode) || SHAPES[0];
  const shows = (f) => shape.fields.includes(f);
  const tiers = current.effort || [];
  const set = (patch) => onChange({ ...current, ...patch });


  return (
    <div className="rounded border border-panel-border bg-field/30 p-3">
      <div className="mb-2 text-[11px] font-medium text-fg">How this model reasons</div>

      <div className="mb-3 flex flex-wrap gap-1">
        {SHAPES.map((s) => (
          <button key={s.mode} type="button" title={s.hint}
            onClick={() => onChange(forShape(s.mode, current))}
            className={`rounded border px-2 py-1 text-[11px] ${
              mode === s.mode ? 'border-orange bg-orange/15 text-orange'
                : 'border-panel-border text-fg-muted hover:text-fg'}`}>
            {s.label}
          </button>
        ))}
      </div>
      <p className="mb-3 text-[11px] text-fg-muted">{shape.hint}</p>

      {shows('effort') && (
        <div className="mb-3">
          <div className="mb-1 text-[11px] text-fg-muted">
            Levels this host offers — weakest to strongest
          </div>
          <div className="flex flex-wrap gap-1">
            {EFFORT_TIERS.map((t) => (
              <button key={t} type="button" onClick={() => onChange(toggleTier(current, t))}
                className={`rounded border px-2 py-0.5 font-mono text-[11px] ${
                  tiers.includes(t) ? 'border-orange bg-orange/15 text-orange'
                    : 'border-panel-border text-fg-muted hover:text-fg'}`}>
                {t}
              </button>
            ))}
          </div>
          {!tiers.length && (
            <p className="mt-1 text-[11px] text-amber-400">
              Named levels needs at least one.
            </p>
          )}
        </div>
      )}

      
      {shows('budget') && (
        <div className="mb-3 flex gap-3">
          {/* `min`/`max`, the keys `spec.from_dict` reads and `to_dict` writes.
              Spelled out they round-tripped to nothing: both boxes blank, and a
              typed ceiling fell back to 1024/32000. */}
          {[['min', 'Smallest budget accepted'], ['max', 'Largest budget accepted']]
            .map(([key, label]) => (
              <div key={key}>
                <div className="mb-1 text-[11px] text-fg-muted">{label}</div>
                <input type="number" min={0}
                  value={current.budget?.[key] ?? ''}
                  onChange={(e) => set({ budget: {
                    ...(current.budget || {}),
                    [key]: e.target.value === '' ? undefined : Number(e.target.value) } })}
                  className="w-28 rounded border border-field-border bg-field px-2 py-1 font-mono text-[11px] text-fg" />
              </div>
            ))}
        </div>
      )}

      <div className="flex flex-wrap gap-4">
        {shows('can_disable') && (
          <Tri label="Thinking can be turned off"
               hint="Measured, not assumed: declaring 'no' without testing cost ~80 thinking tokens per off-request on four Gemini rows."
               value={current.can_disable ?? null}
               onChange={(v) => set({ can_disable: v })} />
        )}
      </div>
    </div>
  );
}

// What the reasoning window compares across hosts. Everything else the table can
// show is cost or a limit, and belongs to Configure.
const REASONING_ROWS = new Set(['levels', 'token budget', 'off switch']);

// A per-host rate: empty defers to the shared row, whose resolved value is the placeholder;
// typing pins it to this host (orange), clearing hands it back.
function RateCell({ host, field, resolved, patched, onWrite }) {
  const [text, setText] = useState(null);
  const shown = text ?? (patched != null ? String(patched) : '');

  const commit = (raw) => {
    const trimmed = raw.trim();
    if (!trimmed) { onWrite(null); return; }
    const n = Number(trimmed);
    if (!Number.isNaN(n) && n >= 0) onWrite(n);
  };

  return (
    <input
      value={shown}
      inputMode="decimal"
      placeholder={resolved === undefined || resolved === null ? '—' : String(resolved)}
      title={patched != null
        ? `Set for ${host} alone. Clear to fall back to the shared row.`
        : `Inherited. Type a number to price ${host} differently.`}
      onChange={(e) => { setText(e.target.value); commit(e.target.value); }}
      onBlur={() => setText(null)}
      className={`w-24 rounded border bg-field px-1.5 py-0.5 font-mono text-[11px] focus:border-orange ${
        patched != null ? 'border-orange/60 text-orange'
          : `border-field-border/50 text-fg ${resolved == null
              ? 'placeholder:text-fg-muted/50' : 'placeholder:text-fg'}`}`} />
  );
}

// What models.dev states for this rate on this host, or null: off the catalog — where a figure
// shown is the default config's or nothing's — no price is ever filled in for the admin.
export function catalogRate(view, rate) {
  const stated = view?.inferred?.[rate];
  return view?.catalog && typeof stated === 'number' ? stated : null;
}

function HostsPanel({ model, row, provider, providers, onRow, onViews, onHostAdded, reasoningOnly = false }) {
  const modelsDev = useContext(ModelsDevContext);
  const [views, setViews] = useState({});
  const [loading, setLoading] = useState(false);
  const [reviewing, setReviewing] = useState(null);

  // Only the hosts that serve it; the table's map cell carries the full set.
  const ids = row?.host_id && typeof row.host_id === 'object' ? row.host_id : {};
  const hosts = useMemo(() => Object.keys(ids).filter((h) => ids[h]).sort(),
    [JSON.stringify(ids)]);
  const unused = (providers || []).filter((p) => !ids[p]);
  // The host it is served as needs an id before anything resolves, so the picker starts there.
  const [pick, setPick] = useState(() => (provider && unused.includes(provider) ? provider : ''));
  const [pickId, setPickId] = useState('');
  useEffect(() => {
    if (provider && !ids[provider] && (providers || []).includes(provider)) setPick(provider);
  }, [provider]);
  const addHost = () => {
    if (!pick || !pickId.trim()) return;
    const next = { ...row, host_id: { ...ids, [pick]: pickId.trim() } };
    onRow(next);
    setPick(''); setPickId('');
    if (onHostAdded) onHostAdded(next);
  };

  // Debounced: a lookup per keystroke asks the catalog about every prefix of
  // the id on the way to the real one.
  const key = JSON.stringify({ m: model, h: row?.host_id, p: row?.per_provider,
                               r: row?.reasoning, s: row?.supports_reasoning });

  useEffect(() => {
    if (!model || !hosts.length) { setViews({}); return; }
    let live = true;
    setLoading(true);
    const timer = setTimeout(() => {
      modelHosts({ model, row, providers: hosts })
        .then((r) => { if (live) { setViews(r.data.hosts || {}); onViews?.(r.data.hosts || {}); } })
        .catch(() => { if (live) { setViews({}); onViews?.({}); } })
        .finally(() => { if (live) setLoading(false); });
    }, 350);
    return () => { live = false; clearTimeout(timer); };
  }, [key]);

  const setId = (host, id) => {
    const map = { ...(row.host_id || {}) };
    const next = { ...row };
    // Per keystroke, so an empty field is mid-edit, not a withdrawal; id-less hosts drop on save anyway.
    if (id.trim()) map[host] = id.trim(); else delete map[host];
    onRow({ ...next, host_id: map });
  };

  const applyFetch = (host, writes) => {
    onRow(writes.reduce((acc, [path, value]) => writeHostField(acc, host, path, value), row));
    setReviewing(null);
  };

  // `_row_rates` takes a host's rates whole, so input and output move together — seeded only from
  // what models.dev states for this host. Off the catalog nothing is filled in: the save asks for
  // the other half, and a free model is the admin's own 0, never a guessed one.
  const writeRate = (host, field, value) => {
    const patches = { ...(row?.per_provider || {}) };
    const patch = { ...(patches[host] || {}) };

    if (value === null) delete patch[field];
    else patch[field] = value;

    const base = ['input', 'output'];
    if (base.includes(field) && value !== null) {
      for (const other of base) {
        const stated = catalogRate(views[host], other);
        if (patch[other] == null && stated != null) patch[other] = stated;
      }
    } else if (base.includes(field) && value === null
               && base.every((k) => patch[k] == null)) {
      // Both cleared: the host stops pricing itself, rather than half-pricing.
      base.forEach((k) => delete patch[k]);
    }

    if (Object.keys(patch).length) patches[host] = patch; else delete patches[host];
    const next = { ...row };
    if (Object.keys(patches).length) next.per_provider = patches;
    else delete next.per_provider;
    onRow(next);
  };

  const summary = (view) => {
    const r = view.capabilities?.reasoning || {};
    const reasons = !!r.mode && r.mode !== 'none';
    const levels = r.effort?.length ? r.effort.join(', ') : r.mode || '—';
    // A model that does not reason has no off switch: `unmeasured` invites a
    // measurement that cannot be made, and `no` would claim it reasons and cannot stop.
    const off = !reasons ? '—'
      : r.can_disable === true ? 'yes'
      : r.can_disable === false ? 'no'
      : 'unmeasured';
    return { levels, budget: r.budget ? 'yes' : '—', off };
  };

  const ROWS = [
    ['output cap', (v) => v.capabilities?.max_output_tokens,
     'Most tokens one reply may generate on this host. Anthropic counts it against the context window, so reserving a large one leaves less room for the conversation.'],
    ['context', (v) => v.capabilities?.context_window,
     'Prompt plus reply, in tokens. The client uses this to decide when to compact a session.'],
    ['input $/M', (v) => v.pricing?.input, 'USD per million prompt tokens on this host. Type here to correct it for this host alone — `input` and `output` move together, so setting one carries the other.', 'input'],
    ['output $/M', (v) => v.pricing?.output, 'USD per million generated tokens on this host.', 'output'],
    ['cache read $/M', (v) => v.pricing?.cache_read, 'USD per million tokens read from cache on this host.', 'cache_read'],
    ['cache write $/M', (v) => v.pricing?.cache_write, 'USD per million tokens written to cache on this host.', 'cache_write'],
    ['context bands', (v) => (v.pricing?.tiers ? `${v.pricing.tiers.length} band${v.pricing.tiers.length === 1 ? '' : 's'}` : 'flat'),
     'Some hosts charge more once a prompt passes a size — Claude Opus on Vertex doubles above 200k. "flat" means one rate at every length; a band count means long prompts bill higher.'],
    ['levels', (v) => summary(v).levels,
     'The thinking depths this host accepts, weakest first. Asking for one it does not declare is a hard 400, which is why these are read per host rather than assumed.'],
    ['token budget', (v) => summary(v).budget,
     'Whether this host lets you set thinking depth as an exact token count instead of a named level.'],
    ['off switch', (v) => summary(v).off,
     'Whether reasoning can be turned off. "unmeasured" means nobody has proved it either way — the router still attempts the disable, because skipping it would reason and bill in silence.'],
  ];

  return (
    <div className="mb-3 rounded border border-panel-border bg-field/40 p-3">
      <SectionHead help={`The id is what the request sends${modelsDev ? " and the key models.dev files that host's row under, so price, limits and the reasoning ladder all fill from it" : ''}. Clear an id and the host stops serving this model \u2014 a request routed there is refused rather than sent under a guessed id.`}>
        Hosts that serve this model
      </SectionHead>

      {hosts.map((host) => {
        const view = views[host];
        const conflicts = Object.values(view?.fields || {}).filter((f) => f.state === 'conflict').length;
        const primary = host === provider;
        return (
          <div key={host} className="mt-2 flex items-center gap-2">
            {/* The badge is a sibling of the truncating name, not inside it: sharing
                one fixed-width truncate clipped it to "SER…" on a long host name. */}
            <span className="flex w-44 shrink-0 items-center gap-1.5">
              <span className={`truncate font-mono text-xs ${ids[host] ? 'text-fg' : 'text-fg-muted'}`}>
                {host}
              </span>
              {primary && (
                <span title="Published as this — clients see the model as this host's name plus the model name, and tier allow-lists match that string."
                  className={`shrink-0 rounded px-1 py-px font-sans text-[9px] uppercase tracking-wide ${
                    ids[host] ? 'bg-orange/20 text-orange' : 'bg-amber-500/20 text-amber-400'}`}>
                  served as
                </span>
              )}
            </span>
            <input value={ids[host] || ''} onChange={(e) => setId(host, e.target.value)}
              placeholder="clear to stop serving here"
              className="flex-1 rounded border border-field-border bg-field px-2 py-1 font-mono text-xs text-fg focus:border-orange" />
            {modelsDev && (
              <span className="w-24 shrink-0 text-[11px]">
                {view && (view.catalog
                  ? <span className="text-green-400">models.dev ✓</span>
                  : <span className="text-red-400">not found</span>)}
              </span>
            )}
            <span className="w-24 shrink-0 text-right text-[11px]">
              {conflicts > 0
                ? <span className="text-amber-400">{conflicts} conflict{conflicts === 1 ? '' : 's'}</span>
                : view ? <span className="text-fg-muted">no conflicts</span> : null}
            </span>
            {modelsDev && (
              <button type="button" onClick={() => setReviewing(host)} disabled={!view}
                title="Show every field models.dev states for this host, beside yours"
                className="flex shrink-0 items-center gap-1 rounded border border-panel-border px-2 py-1 text-[11px] text-fg-muted hover:text-fg disabled:opacity-40">
                <DownloadCloud className="h-3 w-3" />Review
              </button>
            )}
          </div>
        );
      })}

      {/* Spelled out rather than hovered: `catalog: false` showed as a red "not
          found" with the explanation on a title, so the one state that costs money
          went unread — `moonshotai.kimi-k2-thinking` billed at $0.00 that way. */}
      {hosts.flatMap((host) => (views[host]?.problems || []).map((problem) => (
        <p key={`${host}:${problem.message}`}
          className={`mt-1 flex items-start gap-1 pl-2 text-[11px] ${
            isToDo(problem.message) ? 'text-fg-muted' : problem.severity === 'blocking' ? 'text-red-400' : 'text-amber-400'}`}>
          {isToDo(problem.message) ? <Info className="mt-0.5 h-3 w-3 shrink-0" /> : <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" />}
          <span><span className="font-mono">{host}</span> — {problem.message}</span>
        </p>
      )))}

      {unused.length > 0 && (
        <div className="mt-2 flex items-center gap-2 border-t border-panel-border/60 pt-2">
          <select value={pick} onChange={(e) => setPick(e.target.value)}
            className="w-40 shrink-0 rounded border border-field-border bg-field px-2 py-1 text-xs text-fg focus:border-orange">
            <option value="">add a host…</option>
            {unused.map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
          <input value={pickId} onChange={(e) => setPickId(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addHost(); } }}
            placeholder={pick ? `what ${pick} calls this model` : 'pick a host first'}
            disabled={!pick}
            className="flex-1 rounded border border-field-border bg-field px-2 py-1 font-mono text-xs text-fg focus:border-orange disabled:opacity-40" />
          {/* Both or neither: a host with no id looks served and refuses every
              request sent to it. */}
          <button type="button" onClick={addHost} disabled={!pick || !pickId.trim()}
            title={!pick ? 'Pick a host' : !pickId.trim() ? 'Enter the id that host calls this model' : undefined}
            className="shrink-0 rounded border border-panel-border px-3 py-1 text-[11px] text-fg-muted hover:text-fg disabled:opacity-40">
            Add
          </button>
        </div>
      )}

      {hosts.length > 0 && Object.keys(views).length > 0 && (
        <div className="mt-3 overflow-x-auto">
          <p className="mb-1 text-[11px] text-fg-muted">
            {hosts.length > 1 ? 'What differs by host' : 'What this host resolves to'}
            {loading ? ' · refreshing…' : ''}
          </p>
          <table className="text-[11px]">
            <thead className="text-fg-muted"><tr>
              <th className="pr-3 text-left font-medium" />
              {hosts.map((h) => <th key={h} className="px-2 text-left font-mono font-medium">{h}</th>)}
            </tr></thead>
            <tbody className="font-mono text-fg">
              {(reasoningOnly
                  ? ROWS.filter(([label]) => REASONING_ROWS.has(label))
                  : ROWS).map(([label, read, help, rate]) => {
                const cells = hosts.map((h) => (views[h] ? read(views[h]) : undefined));
                const differs = new Set(cells.map((c) => JSON.stringify(c ?? null))).size > 1;
                return (
                  <tr key={label} className={differs ? 'bg-amber-500/5' : ''}>
                    <td title={help}
                      className="cursor-help whitespace-nowrap pr-3 font-sans text-fg-muted underline decoration-dotted underline-offset-2">
                      {label}
                    </td>
                    {cells.map((c, i) => (
                      <td key={hosts[i]} className="px-2 py-0.5">
                        {rate
                          ? <RateCell host={hosts[i]} field={rate} resolved={c}
                              patched={((row?.per_provider || {})[hosts[i]] || {})[rate]}
                              onWrite={(value) => writeRate(hosts[i], rate, value)} />
                          : (c === undefined || c === null ? '—' : String(c))}
                      </td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {modelsDev && reviewing && views[reviewing] && (
        <FetchReview host={reviewing} view={views[reviewing]}
          onApply={(writes) => applyFetch(reviewing, writes)}
          onClose={() => setReviewing(null)} />
      )}
    </div>
  );
}

/** A stored rate in the unit a provider quotes it (`QUOTED_AS`), rounded clear of float noise. */
export const asQuoted = (stored, factor) =>
  (stored == null ? null : Number((stored / factor).toFixed(6)));
const QUOTED_FIELD = Object.fromEntries(
  Object.entries(QUOTED_AS).map(([stored, quoted]) => [quoted.name, { stored, ...quoted }]));

// Per-host values, shaped like `defaults.py` rows so they paste there verbatim; `rpm` stays
// a table column.
// By modality: a clip is priced by the minute or its tokens, speech by the character, a live
// session by its audio tokens at several times the text rate, or by the minute when the host
// reports no usage (a live transcriber); only a chat has a window.
const HOST_COLS = {
  chat: [
    ['input', 'in $/M'], ['output', 'out $/M'],
    ['cache_read', 'cache r'], ['cache_write', 'cache w'],
    ['context_window', 'context'], ['max_output_tokens', 'max out'],
  ],
  transcription: [['audio_minute', '$/min'], ['audio_hour', '$/hour'], ['input', 'in $/M'], ['output', 'out $/M']],
  speech: [['characters', '$/M chars'], ['characters_1k', '$/1K chars'], ['input', 'in $/M'], ['output', 'out $/M']],
  realtime: [
    ['input', 'text in $/M'], ['output', 'text out $/M'],
    ['input_audio', 'audio in $/M'], ['output_audio', 'audio out $/M'], ['cache_read', 'cache r'],
    ['audio_minute', '$/min'], ['audio_hour', '$/hour'],
  ],
};
const RATE_KEYS = new Set(['input', 'output', 'cache_read', 'cache_write',
                           'input_audio', 'output_audio', 'audio_minute', 'characters']);

function HostValuesPanel({ model, value, resolvedModality, options, onSave, onClose }) {
  const modelsDev = useContext(ModelsDevContext);
  const [row, setRow] = useState(() => ({ ...(value || {}) }));
  const [views, setViews] = useState({});
  const [refetch, setRefetch] = useState(0);
  const [refreshing, setRefreshing] = useState(null);
  const [reviewing, setReviewing] = useState(null);
  // The draft text: the committed value is a number and a number cannot hold "0.", so
  // an input bound to it dropped the decimal point as typed and `0.25` committed as 25.
  const [typing, setTyping] = useState({});
  const providers = options.providers || [];
  const ids = row.host_id && typeof row.host_id === 'object' ? row.host_id : {};
  const served = providers.filter((p) => ids[p]);
  // Ids under a name Settings → Providers does not list, deleted since or never added. Listed so
  // they can be cleared: hidden, the save named `host_id.<it>` while this table showed one host.
  const leftover = providers.length
    ? Object.keys(ids).filter((h) => ids[h] && !providers.includes(h)).sort() : [];
  // The row's own, else the resolved one: an override of a built-in audio model that does not
  // restate its modality showed chat's columns, and no Test live (review #335).
  const modality = [row.modality, resolvedModality].find((m) => HOST_COLS[m]) || 'chat';
  const hostCols = HOST_COLS[modality];

  const key = JSON.stringify({ m: model, h: ids, p: row.per_provider });
  useEffect(() => {
    if (!model || !served.length) { setViews({}); return undefined; }
    let live = true;
    const timer = setTimeout(() => {
      modelHosts({ model, row, providers: served })
        .then((r) => {
          if (!live) return;
          setViews(r.data.hosts || {});
        })
        .catch(() => { if (live) setViews({}); });
    }, 300);
    return () => { live = false; clearTimeout(timer); };
  }, [key, refetch]);

  // The catalog is cached for an hour; one refresh re-reads it for every window using `by_host`.
  const pullCatalog = async () => {
    setRefreshing('working');
    try {
      const r = await refreshCatalog();
      setRefreshing(r.data?.off ? 'models.dev is off' : r.data?.fetched ? 'models.dev re-read' : 'models.dev did not answer');
      setRefetch((n) => n + 1);
    } catch (e) {
      setRefreshing('could not reach the gateway');
    }
    setTimeout(() => setRefreshing(null), 4000);
  };

  const setId = (host, id) => {
    const map = { ...ids };
    if (id.trim()) map[host] = id.trim(); else delete map[host];
    const next = { ...row };
    if (Object.keys(map).length) next.host_id = map; else delete next.host_id;
    setRow(next);
  };

  // The pair moves together as in `writeRate`: seeded only from what models.dev states.
  const setField = (host, field, text) => {
    setTyping((t) => ({ ...t, [`${host}:${field}`]: text }));
    const patches = { ...(row.per_provider || {}) };
    const patch = { ...(patches[host] || {}) };
    const raw = text.trim();
    const n = Number(raw);

    if (!raw || Number.isNaN(n) || n < 0) delete patch[field];
    else patch[field] = n;

    if (RATE_KEYS.has(field)) {
      const base = ['input', 'output'];
      if (base.includes(field) && patch[field] != null) {
        base.forEach((k) => {
          const stated = catalogRate(views[host], k);
          if (patch[k] == null && stated != null) patch[k] = stated;
        });
      }
    }
    if (Object.keys(patch).length) patches[host] = patch; else delete patches[host];
    const next = { ...row };
    if (Object.keys(patches).length) next.per_provider = patches; else delete next.per_provider;
    setRow(next);
  };

  const cellFor = (host, field) => {
    const patch = (row.per_provider || {})[host] || {};
    const view = views[host];
    if (QUOTED_FIELD[field]) {
      const { stored, unit, factor } = QUOTED_FIELD[field];
      const key = `${host}:${field}`;
      const inherited = asQuoted(view?.pricing?.[stored], factor);
      return (
        <input value={typing[key] ?? (asQuoted(patch[stored], factor) ?? '')} inputMode="decimal"
          disabled={!ids[host]}
          onBlur={() => setTyping(({ [key]: _gone, ...rest }) => rest)}
          placeholder={inherited == null ? '—' : String(inherited)}
          onChange={(e) => {
            const text = e.target.value;
            setTyping((t) => ({ ...t, [key]: text }));
            setField(host, stored, text.trim() === '' ? '' : String(Number(text) * factor));
          }}
          title={`${unit} — saved as ${stored} (${FIELD_UNITS[stored]})`}
          className={`w-full rounded border bg-field px-1.5 py-0.5 font-mono text-[11px] disabled:opacity-30 ${
            patch[stored] != null ? 'border-orange/60 text-orange'
              : 'border-field-border/60 text-fg placeholder:text-fg-muted'}`} />
      );
    }
    const resolved = field === 'rpm' ? row.rpm
      : RATE_KEYS.has(field) ? view?.pricing?.[field]
      : view?.capabilities?.[field];
    const set = patch[field];
    // A figure this row states and one models.dev states looked identical here, so a
    // catalog miss read as a catalog hit. Same value, different authority.
    const from = view?.fields?.[field]?.state;
    const theirs = from === 'unset' || from === 'agrees';
    const key = `${host}:${field}`;
    const draft = typing[key];
    return (
      <input value={draft ?? (set ?? '')} inputMode="decimal" disabled={!ids[host]}
        onBlur={() => setTyping(({ [key]: _gone, ...rest }) => rest)}
        // Off, a fallback (a $0 price, the default limits) would read as an answer: say it is owed.
        placeholder={!modelsDev && ids[host] && view?.fields?.[field]?.blocks ? 'required'
          : resolved === undefined || resolved === null ? '—' : String(resolved)}
        onChange={(e) => setField(host, field, e.target.value)}
        title={set != null ? `Set for ${host} alone — clear to inherit`
          : !ids[host] ? 'Not served here'
          : (modelsDev && INHERITED_FROM[from]) || 'Inherited — type to set it for this host'}
        className={`w-full rounded border bg-field px-1.5 py-0.5 font-mono text-[11px] disabled:opacity-30 ${
          set != null ? 'border-orange/60 text-orange'
            : `border-field-border/60 text-fg ${resolved == null
                ? 'placeholder:text-fg-muted/50'
                : theirs ? 'placeholder:text-fg' : 'placeholder:text-fg-muted'}`}`} />
    );
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/70 p-6"
      onClick={onClose}>
      <div className="my-auto w-full max-w-6xl rounded-xl border border-panel-border bg-panel p-5 shadow-xl"
        onClick={(e) => e.stopPropagation()}>
        <div className="mb-3 flex items-start justify-between gap-4">
          <div>
            <h3 className="font-semibold text-fg">
              Configure · <span className="font-mono text-sm">{model}</span>
            </h3>
            <p className="mt-1 text-xs text-fg-muted">
              {modelsDev ? 'Ids and per-host values. Reasoning is its own column.'
                : 'Fill in the id, prices, context and max out for the host that serves this model.'}
            </p>
          </div>
          <span className="flex shrink-0 items-center gap-2">
            {modelsDev && refreshing && refreshing !== 'working' && (
              <span className="text-[11px] text-fg-muted">{refreshing}</span>
            )}
            {modelsDev && (
              <button type="button" onClick={pullCatalog} disabled={refreshing === 'working'}
                title="Re-read models.dev now. It is cached for an hour, so a price, limit or ladder that changed today is not picked up until then. Refreshes this window and Reasoning both."
                className="inline-flex items-center gap-1 rounded border border-panel-border px-2 py-1 text-[11px] text-fg-muted hover:text-fg disabled:opacity-50">
                <RefreshCw className={`h-3 w-3 ${refreshing === 'working' ? 'animate-spin' : ''}`} />
                {refreshing === 'working' ? 'reading…' : 'Re-read models.dev'}
              </button>
            )}
            <button onClick={onClose} className="p-1 text-fg-muted hover:text-fg">
              <X className="h-4 w-4" />
            </button>
          </span>
        </div>

        <div className="overflow-x-auto rounded border border-panel-border">
          <table className="w-full text-[11px]">
            <thead className="bg-panel text-fg-muted">
              <tr className="border-b border-panel-border">
                <th className="w-44 px-2 py-1.5 text-left font-medium">provider</th>
                <th className="w-56 px-2 py-1.5 text-left font-medium">id on that host</th>
                {hostCols.map(([, label]) => (
                  <th key={label} className="px-2 py-1.5 text-left font-medium">{label}</th>
                ))}
                <th className="w-20 px-2 py-1.5 text-left font-medium">bands</th>
                {modelsDev && <th className="w-28 px-2 py-1.5 text-left font-medium" />}
              </tr>
            </thead>
            <tbody>
              {[...providers, ...leftover].map((host) => {
                const view = views[host];
                const bands = ((row.per_provider || {})[host] || {}).tiers
                  || (view?.pricing?.tiers) || [];
                const conflicts = Object.values(view?.fields || {})
                  .filter((f) => f.state === 'conflict').length;
                return (
                  <tr key={host} className={`border-b border-panel-border/40 ${ids[host] ? '' : 'opacity-50'}`}>
                    <td className="px-2 py-1 font-mono text-fg">
                      {host}{' '}
                      {leftover.includes(host) && (
                        <span className="font-sans text-red-400"
                          title="Settings → Providers has no provider by this name. Clear the id, or add the provider there.">
                          not in Providers
                        </span>
                      )}
                      {/* Two states: the id is the whole lookup, so there is no third
                          case where the model's name found the row and the id is
                          unconfirmed. */}
                      {modelsDev && ids[host] && view && (view.catalog
                        ? <span className="text-green-400" title="models.dev files this host under this exact id">✓</span>
                        : <span className="text-red-400" title="models.dev has no row under this id — no price, no limits and no SDK binding come from the catalog, so this row has to state them. Open Review for what answers instead.">✗</span>)}
                    </td>
                    <td className="px-2 py-1">
                      <input value={ids[host] || ''} placeholder="not served here"
                        onChange={(e) => setId(host, e.target.value)}
                        className="w-full rounded border border-field-border bg-field px-1.5 py-0.5 font-mono text-[11px] text-fg placeholder:text-fg-muted/50 focus:border-orange" />
                    </td>
                    {hostCols.map(([field]) => (
                      <td key={field} className="px-2 py-1">{cellFor(host, field)}</td>
                    ))}
                    <td className="px-2 py-1 font-mono text-fg-muted">
                      {bands.length ? `${bands.length}` : '—'}
                    </td>
                    {modelsDev && (
                      <td className="px-2 py-1">
                        {/* A re-read moves what is inherited; a value pinned here still
                            wins, by design. This is where you take theirs instead. */}
                        <button type="button" onClick={() => setReviewing(host)} disabled={!view}
                          title="Every field models.dev states for this host, beside yours"
                          className="inline-flex items-center gap-1 rounded border border-panel-border px-2 py-0.5 text-[10px] text-fg-muted hover:text-fg disabled:opacity-30">
                          <DownloadCloud className="h-3 w-3" />
                          {conflicts > 0 ? <span className="text-amber-400">{conflicts}</span> : 'Review'}
                        </button>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* A row's own rates REPLACE the catalog's rather than merge, so pricing one
            host also drops the context bands models.dev publishes for it and long
            prompts bill flat. Measured on anthropic/claude-sonnet-4 at 400,000 tokens:
            catalog $6.00/M against a row's own $3.00/M. Shown, not resolved — which of
            the two is right is the admin's, and the same warning existed before a
            cleanup removed it with the component it lived on. */}
        {modelsDev && providers.filter((host) => (views[host]?.masked_bands || []).length > 0).map((host) => {
          const band = views[host].masked_bands[0];
          return (
            <p key={`${host}:masked`} className="mt-2 flex items-start gap-1 text-[11px] text-amber-400">
              <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" />
              <span>
                <span className="font-mono">{host}</span> — this row sets its own price, which
                replaces the catalog entirely, so the band models.dev publishes above{' '}
                {Number(band.size).toLocaleString()} tokens (${band.input}/M in, ${band.output}/M out)
                is not applied. Long prompts bill at the flat rate. Take that host's bands in
                Review to keep it.
              </span>
            </p>
          );
        })}

        <p className="mt-2 text-[11px] text-fg-muted">
          {modelsDev
            ? 'An id is what the request sends and the key models.dev files that host under \u2014 blank means the model is not served there. Empty numbers are inherited; the figure shown is what that host resolves to. A re-read moves the inherited ones \u2014 a value you have set keeps winning until you take theirs in Review.'
            : 'An id is what the request sends \u2014 blank means the model is not served there. Empty numbers are inherited; the figure shown is what that host resolves to.'}
        </p>

        {/* The one door an audio row always has: Reasoning is closed to a model that does not
            reason, and that is every audio model. Tests the document on screen, unsaved. */}
        {modality !== 'chat' && row.provider && ids[row.provider] && (
          <AudioSection row={row} model={model} provider={row.provider} kind={modality} />
        )}

        {modelsDev && reviewing && views[reviewing] && (
          <FetchReview host={reviewing} view={views[reviewing]}
            onClose={() => setReviewing(null)}
            onApply={(writes) => {
              setRow(writes.reduce((acc, [path, value]) =>
                writeHostField(acc, reviewing, path, value), row));
              setReviewing(null);
            }} />
        )}

        <div className="mt-3 flex items-center gap-2 border-t border-panel-border pt-3">
          <button onClick={() => onSave({ ...row, __replace: true })}
            className="flex items-center gap-1 rounded bg-orange px-4 py-1.5 text-sm text-fg hover:bg-orange-hover">
            <Save className="h-4 w-4" />Apply
          </button>
          <button onClick={onClose}
            className="rounded border border-panel-border px-3 py-1.5 text-sm text-fg-muted hover:text-fg">
            Cancel
          </button>
          <span className="text-xs text-fg-muted">applies to this row; save the section to persist</span>
        </div>
      </div>
    </div>
  );
}

function ModelPanel({ model, provider, value, options, onSave, onClose, onConfigure }) {
  const modelsDev = useContext(ModelsDevContext);
  const [text, setText] = useState(() => JSON.stringify(value || {}, null, 2));
  const [check, setCheck] = useState({ problems: [], preview: null, error: null });
  const [busy, setBusy] = useState(false);
  // The raw document is the escape hatch, not the entry point.
  const [advanced, setAdvanced] = useState(false);
  const [hostViews, setHostViews] = useState({});
  const [showing, setShowing] = useState('');   // '' = the row itself

  let parsed = null, syntax = null;
  try { parsed = JSON.parse(text || '{}'); } catch (e) { syntax = e.message; }
  const row = parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : null;
  // The resolved per-host ladder narrowed by `allowed_effort`, as `reasoning_spec()`
  // narrows it; the row is the fallback until the host view arrives, not the source.
  const allowedEffort = row?.allowed_effort || [];
  const ladder = hostViews[provider]?.capabilities?.reasoning?.effort
    || row?.reasoning?.effort || [];
  const levels = allowedEffort.length
    ? ladder.filter((l) => allowedEffort.includes(l))
    : ladder;
  const previewLevel = levels.includes(row?.reasoning?.default) ? row.reasoning.default : levels[levels.length - 1];

  // Problems and body both come from the router, so this is what would be sent.
  useEffect(() => {
    if (!row) return;
    let live = true;
    previewReasoning({ provider, model, row, effort: previewLevel })
      .then((r) => { if (live) setCheck({ problems: r.data.problems || [], preview: r.data.preview, error: r.data.error }); })
      .catch((e) => { if (live) setCheck({ problems: [], preview: null, error: e.message }); });
    return () => { live = false; };
  }, [text, previewLevel, provider, model]);

  // Recorded, not inferred: bedrock and anthropic speak the same API but a model
  // keeps neither its id nor its ladder moving between them.
  const declaredFor = row?.declared_for;
  const mismatch = declaredFor && provider && declaredFor !== provider;
  const hosts = Object.keys(row?.host_id && typeof row.host_id === 'object' ? row.host_id : {})
    .filter((h) => row.host_id[h]).sort();

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/60 p-6" onClick={onClose}>
      <div className="my-auto w-full max-w-3xl rounded-xl border border-panel-border bg-panel p-6 shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="mb-4 flex items-start justify-between gap-4">
          <div>
            <h3 className="font-semibold text-fg">{model ? <>Model · <span className="font-mono text-sm">{model}</span></> : 'New model'}</h3>
            <p className="mt-1 text-xs text-fg-muted">
              Served as <span className="font-mono">{provider || '—'}</span>
            </p>
          </div>
          <button onClick={onClose} className="p-1 text-fg-muted hover:text-fg"><X className="h-4 w-4" /></button>
        </div>

        {mismatch && (
          <p className="mb-3 inline-flex items-center gap-1 text-xs text-amber-400">
            <AlertTriangle className="h-3 w-3 shrink-0" />
            Reasoning was declared for <span className="font-mono">{declaredFor}</span>, not{' '}
            <span className="font-mono">{provider}</span> — Review it below.
          </p>
        )}

        {/* `reasoningOnly`: the money rows moved to Configure, and a price warning
            naming a host this model is not served as had no business here. */}
        <HostsPanel reasoningOnly model={model} row={row || {}} provider={provider}
          providers={options.providers || []} onViews={setHostViews}
          onRow={(next) => setText(JSON.stringify(next, null, 2))}
          // Off, a host's prices and limits are typed in by hand: its window is the next step.
          onHostAdded={!modelsDev && onConfigure ? onConfigure : undefined} />

        {/* Only once the row names an id for the host it is served as: the catalog is
            keyed on that id, and before it exists the section said "the catalog states
            nothing for this host", blaming models.dev for a field nobody filled in.
            Correcting the catalog is an override and lives in Advanced. */}
        {!syntax && parsed && parsed.host_id?.[provider] && (
          <ReasoningSection row={parsed} model={model} provider={provider} hostViews={hostViews}
            onRow={(next) => setText(JSON.stringify(next, null, 2))} />
        )}



        {/* Outside Advanced: hiding the reason behind a toggle makes a blocked
            Apply unexplainable. */}
        {syntax && <p className="mt-2 flex items-start gap-1 text-xs text-red-400">
          <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" />Not valid JSON — {syntax}
        </p>}

        {check.problems.map((p) => (
          <p key={p} className={`mt-2 flex items-start gap-1 text-xs ${modelsDev ? 'text-amber-400' : 'text-fg-muted'}`}>
            {modelsDev ? <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" /> : <Info className="mt-0.5 h-3 w-3 shrink-0" />}{p}
          </p>
        ))}

        <button type="button" onClick={() => setAdvanced(!advanced)}
          className={`mt-3 flex w-full items-center gap-2 rounded border px-3 py-2 text-left text-xs ${
            advanced ? 'border-orange/50 bg-orange/5 text-fg' : 'border-panel-border bg-field/40 text-fg-muted hover:text-fg'}`}>
          <ChevronRight className={`h-3.5 w-3.5 shrink-0 transition-transform ${advanced ? 'rotate-90' : ''}`} />
          <span className="font-medium">Advanced configuration</span>
          <span className="text-[11px] text-fg-muted">raw document · per-host result · request body</span>
        </button>

        {advanced && (
          <div className="mt-2">
            {/* Per-host views are shown, not typed: a merged document has
                nowhere to write back to. */}
            <div className="mb-2 flex flex-wrap items-center gap-1">
              {[['', 'What is saved', true], ...hosts.map((h) => [h, `What ${h} gets`, false])]
                .map(([id, label, editable]) => (
                  <button key={id || 'row'} type="button"
                    onClick={() => setShowing(id)}
                    title={editable ? 'The one document that is stored. Everything else is worked out from it.'
                      : `Worked out for ${id}: built-ins, then models.dev for that host, then what is saved, then that host's corrections.`}
                    className={`inline-flex items-center gap-1 rounded border px-2 py-0.5 text-[11px] ${
                      showing === id ? 'border-orange bg-orange/15 text-orange'
                        : 'border-panel-border text-fg-muted hover:text-fg'}`}>
                    {editable ? <Pencil className="h-2.5 w-2.5" /> : <Eye className="h-2.5 w-2.5" />}
                    {label}
                  </button>
                ))}
            </div>

            {showing === '' ? (
              <>
                <textarea value={text} onChange={(e) => setText(e.target.value)} spellCheck={false} rows={16}
                  className={`w-full rounded border bg-field px-3 py-2 font-mono text-[11px] leading-relaxed text-fg focus:outline-none ${syntax ? 'border-red-500' : 'border-field-border focus:border-orange'}`} />
                <p className="mt-1 text-[11px] text-fg-muted">
                  The only document that is stored. Fields here apply to every host; a value for one
                  host alone sits under <span className="font-mono">per_provider</span>, which is
                  what Review writes.
                </p>

                {/* The guarded way into the `reasoning` block above: both write the
                    same field, but this one cannot express a spec `validate()` would
                    reject. It sits in Advanced because it is an override, like the rest. */}
                {!syntax && parsed && (
                  <div className="mt-3 rounded border border-panel-border bg-field/40 p-3">
                    <div className="mb-1 text-[11px] font-medium text-fg">
                      Correct what the catalog says
                    </div>
                    <p className="mb-2 text-[11px] text-fg-muted">
                      Only where models.dev is absent or provably wrong for this host. A
                      value typed beside a correct one is pinned, and goes stale the day
                      the host changes.
                    </p>
                    <ReasoningForm spec={parsed.reasoning}
                      onChange={(next) => setText(JSON.stringify(
                        { ...parsed, reasoning: next }, null, 2))} />
                    {/* The one row field with no other control and real teeth: pinned
                        False on ten built-ins, and a model that refuses a temperature
                        fails every call until this says so. Unmeasured drops the key,
                        so the catalog underneath still answers. */}
                    <div className="mt-3 border-t border-panel-border pt-3">
                      <Tri label="Accepts a temperature"
                        hint="Whether the weights take one at all. Every Claude 4.6+ and the o-series refuse it; a model that does fails every request until this says no."
                        value={parsed.supports_temperature ?? null}
                        onChange={(v) => {
                          const next = { ...parsed };
                          if (v === null) delete next.supports_temperature;
                          else next.supports_temperature = v;
                          setText(JSON.stringify(next, null, 2));
                        }} />
                    </div>
                  </div>
                )}
              </>
            ) : (
              <>
                <pre className="max-h-80 w-full overflow-auto rounded border border-field-border bg-field px-3 py-2 font-mono text-[11px] leading-relaxed text-fg">
{JSON.stringify(hostViews[showing]?.capabilities ?? {}, null, 2)}
                </pre>
                <p className="mt-1 text-[11px] text-fg-muted">
                  Worked out, not stored. Set for this host alone:{' '}
                  {hostViews[showing]?.overridden?.length
                    ? <span className="font-mono text-fg">{hostViews[showing].overridden.join(', ')}</span>
                    : 'nothing yet'}
                  <Hint text={'Built-ins, then models.dev for this host, then the row, then this host\u2019s corrections. A merge result has nowhere to write back to \u2014 edit the row, or use Review to correct this host alone.'} />
                </p>
              </>
            )}

            <div className="mt-3 rounded border border-panel-border bg-field/40 p-3">
              <p className="mb-2 text-[11px] text-fg-muted">
                Request body{previewLevel ? <> at <span className="font-mono text-fg">{previewLevel}</span></> : null}
                <Hint text="Built by the router itself, not re-rendered here — what you see is what goes on the wire." />
              </p>
              <pre className="overflow-x-auto text-[11px] leading-relaxed text-green-400">
{check.error ? `\u2717 ${check.error}` : JSON.stringify(check.preview ?? {}, null, 2)}
              </pre>
            </div>
          </div>
        )}

        <div className="mt-4 flex items-center gap-2 border-t border-panel-border pt-3">
          {/* `declared_for` is dropped on save: hosts are the axis now, so the
              stamp says nothing the table above does not. */}
          <button onClick={() => { setBusy(true);
            onSave({ ...(({ declared_for, ...rest }) => rest)(row), __replace: true }); }}
            disabled={busy || !row || check.problems.length > 0}
            title={syntax ? 'Fix the JSON first' : check.problems.length ? 'Fix the problems above first' : undefined}
            className="flex items-center gap-1 rounded bg-orange px-4 py-1.5 text-sm text-fg hover:bg-orange-hover disabled:opacity-40">
            <Save className="h-4 w-4" />Apply
          </button>
          <button onClick={onClose} className="rounded border border-panel-border bg-panel px-3 py-1.5 text-sm text-fg-muted hover:text-fg">Cancel</button>
          <span className="text-xs text-fg-muted">applies to this row; save the section to persist</span>
        </div>
      </div>
    </div>
  );
}

// One width for every yes/no column, so a row of ticks lines up instead of each
// sizing to its own label — `Tools` came out at 44px beside `JSON mode` at 82px.
const boolCol = (col) => col.type === 'bool';

// Quiet time before the table re-resolves an edited models section: one request per pause, not per key.
const RESOLVE_DEBOUNCE_MS = 400;

// What a row shows: built-in, then what it resolves to, then the row itself, with `reasoning` one
// key deep as `_layered` merges it — flat, a row naming only `default` hid the catalog's ladder.
// The row last, since a resolution trails an edit by one request.
// How a model is charged, as the bill charges it (`pricing.billed_unit`): a per-minute or
// per-character price is the whole price, otherwise tokens. Beside the name, so an admin sees
// the unit without opening Configure — filling the wrong box saves fine and bills wrong.
const BILLING_LABEL = {
  minute: (r) => `per min · $${r.audio_minute}${r.audio_minimum_seconds ? ` · min ${r.audio_minimum_seconds}s` : ''}`,
  character: (r) => `per 1M chars · $${r.characters}`,
  token: (r) => `per 1M tokens · $${r.input ?? 0} / $${r.output ?? 0}`
    + (r.input_audio != null || r.output_audio != null
      ? ` · audio $${r.input_audio ?? r.input ?? 0} / $${r.output_audio ?? r.output ?? 0}` : ''),
};
const BILLING_SOURCE = {
  row: 'set on this row', pricing: 'from the Pricing settings', registered: 'registered at runtime',
  'models.dev': 'from models.dev', 'built-in': 'from the built-in price table',
};

function BillingBadge({ billing }) {
  const modelsDev = useContext(ModelsDevContext);
  if (!billing) return null;
  if (billing.unit === 'unpriced') {
    // Off, a new model starts with no price: something to fill in, not a fault.
    return modelsDev ? (
      <span title="Nothing prices this model on this host, so calls to it are refused. Set its price in Configure."
        className="mt-0.5 inline-block rounded border border-red-400/40 px-1 font-sans text-[10px] text-red-400">
        unpriced
      </span>
    ) : (
      <span title="Set its price in Configure."
        className="mt-0.5 inline-block rounded border border-panel-border px-1 font-sans text-[10px] text-fg-muted">
        price needed
      </span>
    );
  }
  return (
    <span title={`Billed per ${billing.unit} — ${BILLING_SOURCE[billing.source] || 'resolved'}`}
      className="mt-0.5 inline-block rounded border border-panel-border px-1 font-sans text-[10px] text-fg-muted">
      {BILLING_LABEL[billing.unit](billing.rates || {})}
    </span>
  );
}

function displayRow(builtIn, settled, own) {
  return { ...builtIn, ...settled, ...own,
           ...(own.reasoning && settled.reasoning
             ? { reasoning: { ...settled.reasoning, ...own.reasoning } } : {}) };
}

function GroupSection({ group, onChanged, extraIds = [], dynamicOptions = {}, referencedBy = null }) {
  const [ov, setOv] = useState(clone(group.override));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [query, setQuery] = useState('');
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState(null);   // { rid, ids, values } for the reasoning panel
  const [note, setNote] = useState(null);        // what an imported file changed
  // An imported file, already applied to the editor. `base` is what the section held
  // before it landed, so unticking a row can put that row back.
  const [imported, setImported] = useState(null);
  const [picked, setPicked] = useState(null);
  const fileRef = useRef(null);

  const customizedOnlyKey = `admin_settings_customized_only_${group.key}`;
  const [customizedOnly, setCustomizedOnly] = useState(() => {
    const v = localStorage.getItem(customizedOnlyKey);
    return v === null ? true : v === 'true';
  });
  useEffect(() => { localStorage.setItem(customizedOnlyKey, String(customizedOnly)); }, [customizedOnly, customizedOnlyKey]);

  useEffect(() => {
    setOv(clone(group.override)); setError(null); setNote(null);
    setImported(null); setPicked(null);
  }, [group.override]);

  // The whole section as a file, and back again. The section save already takes any row of the
  // group, so this needs no route of its own. The SAVED section: the editor holds whatever key
  // was typed in plain text until a save masks it, and a half-reviewed import besides.
  const exportJson = () => {
    const stamp = new Date().toISOString();
    downloadJson(fileNameFor(group.key, stamp), groupFileBody(group.key, group.override, stamp));
  };

  // Edits made since the file landed: the section is no longer what its ticks alone produce.
  const editedSinceImport = () => imported && !sameRows(ov, applySelection(imported.base, imported.value, {
    added: [...picked.added], changed: [...picked.changed], dropped: [...picked.dropped] }));

  const importJson = async (file) => {
    setError(null);
    // Every file is reviewed against the SAVED section: against unsaved edits, a key typed but not
    // saved read as a change the file's mask then overwrote, and the save restored the old key.
    // A second file replaces the review under way rather than stacking on it.
    const discarding = imported ? editedSinceImport() : dirty;
    if (discarding && !window.confirm('Importing reviews the file against the saved section and '
      + 'discards the unsaved edits on screen — a typed or pasted key included. Continue?')) return;
    const base = clone(imported ? imported.base : group.override);
    const read = readGroupFile(await file.text(), group.key, base);
    if (read.error) { setError(read.error); return; }
    // Reviewed before it is applied, and applied before it is saved. A save is a
    // REPLACE, so an omitted row is a deletion — and some rows hold a credential
    // that exists nowhere else.
    setImported({ ...read, base });
    setPicked({ added: new Set(read.added), changed: new Set(read.changed),
                dropped: new Set(read.dropped) });
    setOv(applySelection(base, read.value, read));
    setNote(summarise({ ...read, ...bySign([...read.added, ...read.changed, ...read.dropped],
                                           { base, value: read.value }, hasDefault) }));
  };

  // Each row's tick writes straight through to the editor, so the table always shows
  // what would be saved rather than a promise of it.
  const toggle = (kind, name) => {
    const on = !picked[kind].has(name);
    const next = { ...picked, [kind]: new Set(picked[kind]) };
    if (on) next[kind].add(name); else next[kind].delete(name);
    setPicked(next);
    setOv((prev) => toggleRow(prev, imported, kind, name, on));
    setNote(applied({ ...bySign([...next.added, ...next.changed, ...next.dropped], imported, hasDefault),
                      unmatchedMasks: imported.unmatchedMasks }));
  };
  const hasDefault = (name) => group.defaults?.[name] !== undefined;

  const revertImport = () => {
    if (editedSinceImport() && !window.confirm('Revert the import? The edits made since the file '
      + 'was loaded go with it.')) return;
    setOv(clone(imported.base)); setImported(null); setPicked(null); setNote(null);
  };

  // Every row an import under review touches: which list its tick is in, and what applying it does
  // to the row — the sign is read off the rows, since a file's tombstone is an addition that deletes.
  const marks = useMemo(() => {
    if (!imported) return new Map();
    return new Map(['added', 'changed', 'dropped'].flatMap((kind) => imported[kind].map(
      (name) => [name, { kind, sign: signOf(name, imported, hasDefault(name)) }])));
  }, [imported, group.defaults]);
  const ticked = (name) => {
    const mark = marks.get(name);
    return !!mark && picked[mark.kind].has(name);
  };

  const nested = group.shape === 'nested_scalar';
  const freezeModelIdentity = group.key === 'models';
  const idFields = group.id_fields;
  const modelsDev = useContext(ModelsDevContext);
  const cols = shownColumns(group.columns, modelsDev);
  const dirty = JSON.stringify(ov) !== JSON.stringify(group.override);

  // `group.resolved` answers for the saved section; an unsaved row needs its own resolution or it
  // shows no ladder. On failure the saved one stays on screen.
  const [liveResolved, setLiveResolved] = useState(null);
  useEffect(() => {
    if (group.key !== 'models' || !dirty) { setLiveResolved(null); return undefined; }
    let current = true;
    const timer = setTimeout(() => {
      resolveModels(ov).then((r) => { if (current) setLiveResolved(r.data.resolved); })
        .catch(() => { if (current) setLiveResolved(null); });
    }, RESOLVE_DEBOUNCE_MS);
    return () => { current = false; clearTimeout(timer); };
  }, [ov, dirty, group.key]);
  const resolvedRows = liveResolved || group.resolved;

  const rows = useMemo(() => {
    const out = [];
    if (nested) {
      const provs = new Set([...Object.keys(group.defaults || {}), ...Object.keys(ov || {})]);
      for (const p of provs) {
        const models = new Set([...Object.keys(group.defaults?.[p] || {}), ...Object.keys(ov?.[p] || {})]);
        for (const m of models) {
          const overridden = ov?.[p]?.[m] !== undefined;
          const val = overridden ? ov[p][m] : group.defaults?.[p]?.[m];
          const one = { [cols[0].field]: val };
        out.push({ rid: `${p} ${m}`, ids: { provider: p, model: m },
                   values: one, display: one, overridden });
        }
      }
    } else {
      // extraIds: a model added in a sibling model-keyed table shows as a
      // fillable row here too.
      const ids = new Set([...Object.keys(group.defaults || {}), ...Object.keys(ov || {}), ...extraIds,
                           // A row this import deletes, or whose addition was declined, is gone
                           // from `ov`; kept on screen so the tick that brings it back is reachable.
                           ...marks.keys()]);
      for (const id of ids) {
        // An import's row held by neither the editor nor the built-ins: shown from `base` (on its
        // way out) or the file (declined), and read-only, since an edit would write a row the review
        // says is gone. Marked only — a row deleted in the editor is listed by `extraIds` too.
        const ghost = marks.has(id) && ov?.[id] === undefined && group.defaults?.[id] === undefined;
        const overridden = ghost ? true : ov?.[id] !== undefined;
        const ghostRow = ghost ? (imported?.base[id] ?? imported?.value[id] ?? {}) : null;
        const values = ghost ? ghostRow
                             : (overridden ? ov[id] : (group.defaults?.[id] || {}));
        // For display only: `setCell` still writes from the stored row, so nothing shown is pinned
        // unless changed.
        const display = ghost ? { ...ghostRow }
          : displayRow(group.defaults?.[id] || {}, resolvedRows?.[id] || {}, overridden ? ov[id] : {});
        const frozen = ghost || (marks.has(id) && !!values?.__deleted);
        out.push({ rid: id, ids: { [idFields[0].field]: id }, values, display, overridden, frozen });
      }
    }
    out.sort((a, b) => a.rid.localeCompare(b.rid));
    return out;
  }, [group.defaults, ov, nested, cols, idFields, extraIds, marks, imported, resolvedRows]);

  const filtered = rows.filter((r) => !query || Object.values(r.ids).join(' ').toLowerCase().includes(query.toLowerCase()));
  // A tombstone an import is reviewing stays: hidden, its tick could not be reached.
  const notDeleted = filtered.filter((r) => !r.values?.__deleted || marks.has(r.rid));
  // A row the import resets to its built-in stops being modified, and "Modified only" hid its tick.
  const visibleRows = notDeleted.filter((r) => !customizedOnly || r.overridden || marks.has(r.rid));
  const hiddenCount = notDeleted.length - visibleRows.length;

  // Edit a cell — seed the override row from defaults so it stays complete
  const setCell = (ids, field, raw, type) => {
    const value = coerce(raw, type);
    setOv((prev) => {
      const next = clone(prev);
      if (nested) {
        const { provider, model } = ids;
        next[provider] = { ...(next[provider] || {}), [model]: value };
      } else {
        const id = ids[idFields[0].field];
        const baseRow = next[id] || group.defaults?.[id] || {};
        next[id] = { ...baseRow, [field]: value };
      }
      return next;
    });
  };

  // Same seeded base a single cell edit uses, so both paths leave one shape.
  const setRow = (ids, doc) => {
    if (!doc) return;
    setOv((prev) => {
      const next = clone(prev);
      const id = ids[idFields[0].field];
      // `__replace` means the doc IS the row: a shallow spread cannot express a
      // key the panel dropped.
      const { __replace: whole, ...rest } = doc;
      next[id] = whole ? rest : { ...(next[id] || group.defaults?.[id] || {}), ...rest };
      return next;
    });
  };

  const resetRow = (ids) => {
    const name = ids[idFields[0].field];
    // A provider's key is stored nowhere else, and the save drops the reverted row with it.
    if (group.key === 'providers' && ov?.[name]?.api_key && !window.confirm(
      `Revert ${name} to its built-in default?\n\nSaving removes the API key stored for it.`)) return;
    setOv((prev) => {
      const next = clone(prev);
      if (nested) {
        const { provider, model } = ids;
        if (next[provider]) { delete next[provider][model]; if (!Object.keys(next[provider]).length) delete next[provider]; }
      } else {
        delete next[ids[idFields[0].field]];
      }
      return next;
    });
  };

  const hasDefaultRow = (ids) => {
    if (nested) return group.defaults?.[ids.provider]?.[ids.model] !== undefined;
    return group.defaults?.[ids[idFields[0].field]] !== undefined;
  };

  // Models naming this provider, so the confirm can say what breaks: the documents have no join.
  const dependants = (name) => {
    if (!referencedBy) return null;
    const rows = { ...(referencedBy.defaults || {}), ...(referencedBy.override || {}) };
    const served = [], hosted = [];
    for (const [model, row] of Object.entries(rows)) {
      if (!row || row.__deleted) continue;
      if (row.provider === name) served.push(model);
      else if (row.host_id && typeof row.host_id === 'object' && row.host_id[name]) hosted.push(model);
    }
    return served.length || hosted.length ? { served, hosted } : null;
  };

  // A row with a built-in underneath gets a tombstone, which every reader of the group
  // skips — dropping it from the override map would let the default answer again.
  const deleteRow = (ids) => {
    const name = ids[idFields[0].field];
    const dep = dependants(name);
    const impact = dep
      ? `\n\n${dep.served.length} model${dep.served.length === 1 ? '' : 's'} `
        + `${dep.served.length === 1 ? 'is' : 'are'} served by it and will have no route`
        + (dep.served.length ? `:\n  ${dep.served.slice(0, 8).join('\n  ')}`
                             + (dep.served.length > 8 ? `\n  …and ${dep.served.length - 8} more` : '')
                             : '')
        + '\n\nSaving removes its id and per-host prices from every model that lists it '
        + `(${dep.served.length + dep.hosted.length}); undoing the delete does not bring them back.`
      : '';
    if (!window.confirm(
      `Delete ${name}?\n\nThis removes it from ${group.label}. `
      + (hasDefaultRow(ids)
          ? 'It has a built-in default, which stays hidden until you undo this.'
          : 'It has no built-in default, so it is gone for good.')
      + impact)) return;
    setOv((prev) => {
      const next = clone(prev);
      if (hasDefaultRow(ids)) next[name] = { __deleted: true };
      else delete next[name];
      return next;
    });
  };

  // The column immediately after the frozen one sits under its drop shadow.
  const firstAfterSticky = (index) =>
    freezeModelIdentity && index > 0 && cols[index - 1]?.field === 'provider';

  // Rows this page changed that are published under a host with no id. The router refuses these
  // on write, but not in a way that names the row. Changed rows only: a model left with no host
  // by a provider's deletion blocked every save of the section, as the router once did.
  const unroutable = useMemo(() => {
    if (group.key !== 'models') return [];
    const loaded = group.override || {};
    return Object.entries(ov || {}).filter(([name, row]) => {
      if (!row || typeof row !== 'object' || row.__deleted) return false;
      if (JSON.stringify(row) === JSON.stringify(loaded[name])) return false;
      // A row with no provider can route nowhere; `_default` is exempt as the fallback, not a
      // model anyone calls.
      if (!row.provider) return name !== '_default';
      const ids = row.host_id;
      return !(typeof ids === 'object' ? ids?.[row.provider] : ids);
    }).map(([name]) => name);
  }, [JSON.stringify(ov), group.key, group.override]);

  // Only what this page changed, against what it loaded: sent whole, a page opened before
  // someone else's save wrote its old copy back and dropped every provider, key and model since.
  const save = async () => {
    const loaded = group.override || {};
    const edited = ov || {};
    const rows = Object.fromEntries(Object.entries(edited)
      .filter(([id, row]) => JSON.stringify(row) !== JSON.stringify(loaded[id])));
    const removed = Object.keys(loaded).filter((id) => !(id in edited));
    setBusy(true); setError(null);
    try {
      await changeSection(group.key, { rows, removed, category: group.category });
      onChanged(`${group.label} saved`);
    }
    catch (e) { setError(e.response?.data?.detail || e.message || 'Save failed'); } finally { setBusy(false); }
  };
  const resetAll = async () => {
    if (!window.confirm(`Reset all ${group.label} overrides to built-in defaults?`)) return;
    setBusy(true); setError(null);
    try { await deleteSetting(group.key); onChanged(`${group.label} reset to defaults`); }
    catch (e) { setError(e.response?.data?.detail || 'Reset failed'); } finally { setBusy(false); }
  };

  return (
    <div className="bg-panel border border-panel-border rounded-xl p-6">
      <div className="flex items-start justify-between gap-4 mb-3">
        <div>
          <h2 className="text-fg font-semibold">{group.label}</h2>
          <p className="text-fg-muted text-xs mt-1">{group.description}</p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button onClick={() => setCustomizedOnly((v) => !v)} role="switch" aria-checked={customizedOnly}
            className="flex items-center gap-2 group">
            <span className={`relative w-8 h-[19px] rounded-full border shrink-0 transition-colors ${customizedOnly ? 'bg-orange border-orange' : 'bg-field border-field-border group-hover:border-orange/50'}`}>
              <span className={`absolute top-[1.5px] left-[1.5px] w-3.5 h-3.5 rounded-full transition-transform ${customizedOnly ? 'translate-x-[13px] bg-white' : 'bg-fg-muted'}`} />
            </span>
            <span className="text-xs text-fg-muted whitespace-nowrap">
              <span className="text-fg font-medium">{customizedOnly ? 'Modified only' : 'Showing all'}</span>
              {' · '}{visibleRows.length} shown
            </span>
          </button>
          <div className="relative">
            <Search className="w-3.5 h-3.5 text-fg-muted absolute left-2 top-1/2 -translate-y-1/2" />
            <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="filter…"
              className="w-40 pl-7 pr-2 py-1.5 bg-field border border-field-border rounded text-fg text-xs focus:border-orange" />
          </div>
          <button onClick={exportJson}
            title={`${dirty ? `Download the saved ${group.label} as JSON — unsaved edits are not included`
                           : `Download ${group.label} as JSON`}${group.key === 'providers'
                ? '. api_key is masked; extra_headers and extra_config are exported as stored.' : ''}`}
            className="flex items-center gap-1 px-2.5 py-1.5 bg-panel border border-panel-border text-fg-muted hover:text-fg rounded text-xs"><Download className="w-3.5 h-3.5" />Export</button>
          <button onClick={() => fileRef.current?.click()} title={`Load ${group.label} from a JSON file`}
            className="flex items-center gap-1 px-2.5 py-1.5 bg-panel border border-panel-border text-fg-muted hover:text-fg rounded text-xs"><Upload className="w-3.5 h-3.5" />Import</button>
          <input ref={fileRef} type="file" accept="application/json,.json" className="hidden"
            onChange={(e) => { const f = e.target.files?.[0]; if (f) importJson(f); e.target.value = ''; }} />
          <button onClick={() => setAdding((v) => !v)} className="flex items-center gap-1 px-2.5 py-1.5 bg-panel border border-panel-border text-fg-muted hover:text-fg rounded text-xs"><Plus className="w-3.5 h-3.5" />Add</button>
          {group.is_overridden && <button onClick={resetAll} disabled={busy} title="Reset all to defaults" className="p-1.5 text-fg-muted hover:text-red-400 rounded disabled:opacity-50"><Trash2 className="w-3.5 h-3.5" /></button>}
        </div>
      </div>

      {adding && <AddRow group={group} dynamicOptions={dynamicOptions} onAdd={(ids, values) => {
        setOv((prev) => {
          const next = clone(prev);
          if (nested) { next[ids.provider] = { ...(next[ids.provider] || {}), [ids.model]: values[cols[0].field] }; }
          else { next[ids[idFields[0].field]] = values; }
          return next;
        });
        setAdding(false);
        // With models.dev off nothing fills a new model in, so its details are asked for at once,
        // unless they were already given in the form's own Configure.
        const configured = Object.values(values.host_id || {}).some(Boolean);
        if (!modelsDev && group.key === 'models' && !configured) {
          setEditing({ ids, values, field: 'per_provider' });
        }
      }} onCancel={() => setAdding(false)} />}

      <div className="overflow-x-auto">
        <table className="w-full min-w-max text-sm">
          <thead>
            <tr className="text-left text-xs text-fg-muted border-b border-panel-border">
              {imported && <th className="py-2 pr-2 w-10 font-medium" title="Changes from the imported file">±</th>}
              {idFields.map((f) => (
                <th key={f.field} className={`py-2 font-medium ${freezeModelIdentity ? 'sticky left-0 z-20 w-80 min-w-80 max-w-80 px-3 bg-panel border-r border-panel-border' : 'pr-3'}`}>
                  {f.label}
                </th>
              ))}
              {cols.map((c, ci) => (
                <th key={c.field}
                  className={`py-2 font-medium ${
                    freezeModelIdentity && c.field === 'provider'
                      ? 'sticky left-80 z-20 w-48 min-w-48 px-3 bg-panel border-r border-panel-border shadow-[4px_0_8px_-6px_rgb(0_0_0_/_0.7)]'
                      : `pr-3 ${boolCol(c) ? 'w-20 min-w-20 text-center' : ''} ${
                          firstAfterSticky(ci) ? 'pl-4' : ''}`}`}>
                  <ColumnHeader col={c} />
                </th>
              ))}
              <th className="sticky right-0 z-10 py-2 pl-3 w-20 bg-panel border-l border-panel-border font-medium text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {visibleRows.map((r) => (
              <tr key={r.rid} className={`border-b border-panel-border/50 ${r.overridden ? 'bg-blue-500/5' : ''}`}>
                {imported && (() => {
                  const mark = marks.get(r.rid);
                  if (!mark) return <td className="py-1.5 pr-2 w-10" />;
                  const on = ticked(r.rid);
                  const tone = { [SIGNS.added]: 'text-emerald-400', [SIGNS.changed]: 'text-fg-muted',
                                 [SIGNS.dropped]: 'text-red-400' }[mark.sign];
                  const title = {
                    [SIGNS.added]: on ? 'added by the imported file — untick to skip it'
                                      : 'skipped — tick to add this row after all',
                    [SIGNS.changed]: on ? 'changed by the imported file — untick to leave it as it was'
                                        : "left as it was — tick to take the file's version",
                    [SIGNS.dropped]: on ? 'deleted by the imported file — untick to keep it'
                                        : 'kept — tick to delete it as the file does',
                  }[mark.sign];
                  return (
                    <td className="py-1.5 pr-2 w-10">
                      <label className="flex items-center gap-1 cursor-pointer" title={title}>
                        <input type="checkbox" checked={on}
                          onChange={() => toggle(mark.kind, r.rid)} className="accent-orange" />
                        <span className={`font-mono text-xs ${tone}`}>{mark.sign}</span>
                      </label>
                    </td>
                  );
                })()}
                {idFields.map((f) => (
                  <td key={f.field} title={r.ids[f.field]} className={`py-1.5 font-mono text-xs text-fg whitespace-nowrap ${freezeModelIdentity ? `sticky left-0 z-10 w-80 min-w-80 max-w-80 px-3 border-r border-panel-border ${r.overridden ? 'bg-surface-2' : 'bg-panel'}` : 'pr-3'}`}>
                    <span className={`${freezeModelIdentity ? 'block truncate' : ''} ${
                      marks.get(r.rid)?.sign === SIGNS.dropped && ticked(r.rid)
                        ? 'line-through text-fg-muted'
                        : marks.get(r.rid)?.sign === SIGNS.added && !ticked(r.rid)
                          ? 'text-fg-muted/50 italic' : ''}`}>{r.ids[f.field]}</span>
                    {group.key === 'models' && <BillingBadge billing={(r.display ?? r.values)?.billing} />}
                  </td>
                ))}
                {cols.map((c, ci) => (
                  <td key={c.field} className={`py-1.5 ${
                    freezeModelIdentity && c.field === 'provider'
                      ? `sticky left-80 z-10 w-48 min-w-48 px-3 border-r border-panel-border shadow-[4px_0_8px_-6px_rgb(0_0_0_/_0.7)] ${r.overridden ? 'bg-surface-2' : 'bg-panel'}`
                      : `pr-3 ${boolCol(c) ? 'w-20 min-w-20 text-center' : ''} ${
                          firstAfterSticky(ci) ? 'pl-4' : ''}`}`}>
                    <fieldset disabled={r.frozen} className="contents">
                      <GroupCell col={c} values={r.display ?? r.values} dynamicOptions={dynamicOptions}
                        onChange={(v, t) => setCell(r.ids, c.field, v, t)}
                        onOpenPanel={() => setEditing({ ids: r.ids, values: r.values, field: c.field })} />
                    </fieldset>
                  </td>
                ))}
                <td className={`sticky right-0 py-1.5 pl-3 text-right bg-panel border-l border-panel-border ${r.overridden ? 'bg-blue-500/10' : ''}`}>
                  <div className="flex items-center justify-end gap-2">
                    {!r.overridden && <span className="text-xs text-gray-600">default</span>}
                    {/* Revert only where there is something to revert TO. On a row
                        the org invented this used to be the delete button wearing
                        a revert label — same icon, same tooltip, one restored a
                        default and the other destroyed the row, with no prompt. */}
                    {r.overridden && !r.frozen && hasDefaultRow(r.ids) && (
                      <button onClick={() => resetRow(r.ids)} title="Revert this row to default" className="inline-flex items-center justify-center p-1.5 text-fg-muted hover:text-fg rounded hover:bg-field">
                        <Undo2 className="w-3.5 h-3.5" />
                      </button>
                    )}
                    {!r.frozen && (
                      <button onClick={() => deleteRow(r.ids)} title={`Delete this ${group.rowNoun || 'row'}`} className="inline-flex items-center justify-center p-1.5 text-fg-muted hover:text-red-400 rounded hover:bg-field">
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {visibleRows.length === 0 && (
              <tr>
                <td colSpan={idFields.length + cols.length + 1} className="py-4 text-center text-fg-muted text-xs">
                  {customizedOnly && hiddenCount > 0 ? (
                    <>No modified {group.label.toLowerCase()} yet — <button onClick={() => setCustomizedOnly(false)} className="text-fg underline decoration-dotted">show all defaults</button> to add one.</>
                  ) : 'No rows'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Which door was used decides which window opens: the price column asks about
          hosts and money, the reasoning column asks about reasoning. */}
      {editing && (editing.field === 'per_provider' ? (
        <HostValuesPanel
          model={editing.ids[idFields[0].field]}
          value={editing.values}
          resolvedModality={resolvedRows?.[editing.ids[idFields[0].field]]?.modality}
          options={dynamicOptions}
          onClose={() => setEditing(null)}
          onSave={(next) => { setRow(editing.ids, next); setEditing(null); }} />
      ) : (
        <ModelPanel
          model={editing.ids[idFields[0].field]}
          provider={editing.values.provider}
          value={editing.values}
          options={dynamicOptions}
          onClose={() => setEditing(null)}
          onConfigure={(next) => { setRow(editing.ids, next); setEditing({ ids: editing.ids, values: next, field: 'per_provider' }); }}
          onSave={(next) => { setRow(editing.ids, next); setEditing(null); }} />
      ))}

      {error && (!modelsDev && String(error).split('; ').every(isToDo)
        ? <p className="mt-3 text-xs text-fg-muted flex items-center gap-1"><Info className="w-3 h-3" />{error}</p>
        : <p className="mt-3 text-xs text-red-400 flex items-center gap-1"><AlertTriangle className="w-3 h-3" />{error}</p>)}
      {imported && (
        <div className="mt-3 flex items-center gap-3 rounded border border-orange/40 bg-orange/5 px-3 py-2">
          <Info className="h-3.5 w-3.5 shrink-0 text-orange" />
          <span className="text-xs text-fg-muted">
            {imported.added.length + imported.changed.length + imported.dropped.length} change(s)
            loaded from the file, shown against each row below. Untick one to leave that row as
            it was — a struck-through row is one this file deletes.
          </span>
          <button onClick={revertImport}
            className="ml-auto shrink-0 px-2.5 py-1 bg-panel border border-panel-border text-fg-muted hover:text-fg rounded text-xs">
            Revert import
          </button>
        </div>
      )}
      {imported && referencedBy && [...marks].filter(([name, m]) => m.sign === SIGNS.dropped && ticked(name))
        .map(([name]) => [name, dependants(name)]).filter(([, dep]) => dep?.served.length)
        .map(([name, dep]) => (
          // The delete button warns about this; an import deleting the same row has to as well.
          <p key={name} className="mt-2 flex items-start gap-1 text-xs text-red-400">
            <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" />
            Deleting {name} leaves {dep.served.length} model{dep.served.length === 1 ? '' : 's'} with
            no route: {dep.served.slice(0, 8).join(', ')}{dep.served.length > 8 ? `, and ${dep.served.length - 8} more` : ''}.
          </p>
        ))}
      {note && <p className="mt-3 text-xs text-fg-muted flex items-start gap-1"><Info className="w-3 h-3 mt-0.5 shrink-0" />{note}</p>}

      {dirty && (
        <div className="sticky bottom-0 z-20 mt-4 flex items-center gap-2 border-t border-panel-border bg-panel/95 py-3 backdrop-blur">
          <button onClick={save} disabled={busy || unroutable.length > 0}
            title={unroutable.length ? `Served as a host with no id: ${unroutable.join(', ')}` : undefined}
            className="flex items-center gap-1 px-4 py-1.5 bg-orange hover:bg-orange-hover text-fg rounded text-sm disabled:opacity-50"><Save className="w-4 h-4" />{busy ? 'Saving…' : 'Save changes'}</button>
          <button onClick={() => { setOv(clone(group.override)); setNote(null); setImported(null); setPicked(null); }} className="px-3 py-1.5 bg-panel border border-panel-border text-fg-muted hover:text-fg rounded text-sm">Discard</button>
          {unroutable.length > 0
            ? <span className="flex items-center gap-1 text-xs text-red-400">
                <AlertTriangle className="h-3 w-3 shrink-0" />
                <span className="font-mono">{unroutable.slice(0, 3).join(', ')}</span>
                {unroutable.length > 3 && ` +${unroutable.length - 3}`}
                {' '}— served as a host with no id, so nothing can be sent there
              </span>
            : <span className="text-xs text-fg-muted">unsaved changes in this section</span>}
        </div>
      )}
    </div>
  );
}

function AddRow({ group, onAdd, onCancel, dynamicOptions = {} }) {
  const modelsDev = useContext(ModelsDevContext);
  const takePanel = (doc) => {
    const { __replace: whole, ...rest } = doc;
    setVals((prev) => (whole ? rest : { ...prev, ...rest }));
  };
  const [ids, setIds] = useState(Object.fromEntries(group.id_fields.map((f) => [f.field, ''])));
  const [vals, setVals] = useState(() => initialCells(group.columns));
  const [error, setError] = useState(null);
  const [editing, setEditing] = useState(null);

  // What models.dev fills in for the row being written, so its boxes and ladder show before it is
  // added rather than reading as unset. Only once the served-as host has an id: before that the
  // answer is `_default`'s guess, not the catalog's. On failure the form shows only what was typed.
  const name = String(ids[group.id_fields[0].field] || '').trim();
  const servedId = vals.host_id && typeof vals.host_id === 'object' ? vals.host_id[vals.provider] : null;
  const [resolved, setResolved] = useState(null);
  useEffect(() => {
    if (group.key !== 'models' || !name || !servedId) { setResolved(null); return undefined; }
    let current = true;
    const timer = setTimeout(() => {
      resolveModels({ [name]: vals })
        .then((r) => { if (current) setResolved(r.data.resolved?.[name] || null); })
        .catch(() => { if (current) setResolved(null); });
    }, RESOLVE_DEBOUNCE_MS);
    return () => { current = false; clearTimeout(timer); };
  }, [group.key, name, JSON.stringify(vals)]);
  const shown = resolved ? displayRow({}, resolved, vals) : vals;

  const submit = () => {
    for (const f of group.id_fields) if (!String(ids[f.field]).trim()) { setError(`${f.label} is required`); return; }
    // Everything the form holds, typed columns coerced over it: copying `group.columns` alone
    // dropped `host_id`, and the model saved with no id.
    const values = { ...vals };
    try {
      // Only what was actually set. A field left alone is not `false` or `0` — it is
      // a question the catalog answers, and storing an answer to it pins one.
      for (const c of group.columns) {
        if (!(c.field in vals)) continue;
        values[c.field] = coerce(vals[c.field], c.type);
      }
    } catch (e) {
      setError('Invalid JSON in one of the fields'); return;
    }
    onAdd(ids, values);
  };

  return (
    <div className="mb-3 p-3 rounded-lg border border-orange/40 bg-orange/5">
      {group.key === 'models' && (modelsDev ? (
        <p className="mb-2 text-[11px] leading-relaxed text-fg-muted">
          Only <strong className="text-fg">Model</strong>, <strong className="text-fg">Served as</strong> and
          one <strong className="text-fg">Host</strong> id are needed. Limits, pricing, modalities and the
          reasoning ladder fill from models.dev once an id is set — open{' '}
          <span className="text-orange">Configure…</span> under Reasoning to add hosts, see what each one
          resolves to, and correct anything that is wrong.
        </p>
      ) : (
        <p className="mb-2 text-[11px] leading-relaxed text-fg-muted">
          Enter <strong className="text-fg">Model</strong> and <strong className="text-fg">Served as</strong>.
          Its id, prices, context and max out go in <strong className="text-fg">Configure</strong>, which
          opens when you add the row.
        </p>
      ))}
      <div className="flex flex-wrap items-end gap-3">
        {group.id_fields.map((f) => (
          <div key={f.field}>
            <label className="block text-xs text-fg-muted mb-1">{f.label}</label>
            <input value={ids[f.field]} onChange={(e) => setIds({ ...ids, [f.field]: e.target.value })}
              className="px-2 py-1 bg-field border border-field-border rounded text-fg text-xs font-mono focus:border-orange" />
          </div>
        ))}
        {shownColumns(group.columns, modelsDev).map((c) => (
          <div key={c.field}>
            <label className="block text-xs text-fg-muted mb-1"><ColumnHeader col={c} /></label>
            <GroupCell col={c} values={shown} dynamicOptions={dynamicOptions}
              onChange={(v) => setVals((prev) => ({ ...prev, [c.field]: v }))}
              onOpenPanel={() => setEditing(c.field)} />
          </div>
        ))}
        <button onClick={submit} className="px-3 py-1.5 bg-orange hover:bg-orange-hover text-fg rounded text-xs">Add row</button>
        <button onClick={onCancel} className="px-3 py-1.5 text-fg-muted hover:text-fg text-xs">Cancel</button>
      </div>
      {error && <p className="mt-2 text-xs text-red-400">{error}</p>}

      {editing && (editing === 'per_provider' ? (
        <HostValuesPanel
          model={ids[group.id_fields[0].field] || ''}
          value={vals}
          resolvedModality={resolved?.modality}
          options={dynamicOptions}
          onClose={() => setEditing(null)}
          onSave={(next) => { takePanel(next); setEditing(null); }} />
      ) : (
        <ModelPanel
          model={ids[group.id_fields[0].field] || ''}
          provider={vals.provider}
          value={vals}
          options={dynamicOptions}
          onClose={() => setEditing(null)}
          onConfigure={(next) => { takePanel(next); setEditing('per_provider'); }}
          onSave={(next) => { takePanel(next); setEditing(null); }} />
      ))}
    </div>
  );
}

const CARD_META = {
  'General':       { icon: SlidersHorizontal, desc: 'Defaults, timeouts, and the config refresh TTL.' },
  'Model Settings':{ icon: Cpu,               desc: 'One row per model — provider, capabilities, rate limit (RPM), and pricing.' },
  'Rate Limits':   { icon: Gauge,             desc: 'Limiter toggle and per-provider/model RPM caps.' },
  'Providers':     { icon: Server,            desc: 'Provider endpoints and credentials.' },
  'Queue (FCFS)':  { icon: ListOrdered,       desc: 'First-come-first-serve queue behavior.' },
  'Logging':       { icon: FileText,          desc: 'Request logging and flush cadence.' },
};
const CARD_ORDER = ['General', 'Model Settings', 'PDF Citations', 'Rate Limits', 'Providers', 'Queue (FCFS)', 'Logging'];

// A card's name as it appears in the URL: `Queue (FCFS)` → `queue-fcfs`.
const sectionSlug = (name) => name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');

export default function Settings() {
  const navigate = useNavigate();
  const [data, setData] = useState({ settings: [], groups: [], custom: [], options: {} });
  const [meta, setMeta] = useState({});
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState(null);
  // The open card lives in the URL: held in state, the sidebar's link to this same page changed
  // nothing and left the card open. Back and reload follow it now too.
  const [params, setParams] = useSearchParams();
  const section = params.get('section');
  const setSelected = (name) => setParams(name ? { section: sectionSlug(name) } : {});
  const [flags, setFlags] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getSettings();
      // `options` carries the choice lists a column names via `options_from`.
      setData({
        settings: res.data.settings || [], groups: res.data.groups || [],
        custom: res.data.custom || [], options: res.data.options || {},
      });
      setMeta({ db: res.data.db, collection: res.data.collection });
    } catch (e) { setMessage({ type: 'error', text: 'Failed to load settings' }); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { getAppConfig().then((r) => setFlags(r.data || {})).catch(() => {}); }, []);

  const onChanged = (text) => { setMessage({ type: 'success', text }); setTimeout(() => setMessage(null), 4000); load(); };
  // A reload unmounts the card and drops unsaved rows: flipping models.dev changes only the options.
  const onModelsDevToggled = async (text) => {
    const res = await getSettings();
    setData((d) => ({ ...d, options: res.data.options || {} }));
    setMessage({ type: 'success', text }); setTimeout(() => setMessage(null), 4000);
  };

  const cards = useMemo(() => {
    const map = {};
    const ensure = (cat) => (map[cat] || (map[cat] = { name: cat, scalars: [], groups: [] }));
    for (const s of data.settings) ensure(s.category).scalars.push(s);
    for (const g of data.groups) ensure(g.category).groups.push(g);   // a card may hold several group tables
    // (custom/uncatalogued DB rows are intentionally not surfaced as a card)

    const present = Object.keys(map);
    const ordered = [...CARD_ORDER.filter((c) => present.includes(c)), ...present.filter((c) => !CARD_ORDER.includes(c))];
    return ordered.map((name) => {
      const card = map[name];
      const groupItems = card.groups.reduce((n, g) => n + Object.keys({ ...(g.defaults || {}), ...(g.override || {}) }).length, 0);
      const groupOverrides = card.groups.reduce((n, g) => n + Object.keys(g.override || {}).length, 0);
      const scalarOverrides = card.scalars.filter((s) => s.is_overridden).length;
      return {
        ...card,
        count: card.scalars.length + groupItems,
        overridden: scalarOverrides + groupOverrides,
      };
    });
  }, [data]);

  const current = cards.find((c) => sectionSlug(c.name) === section) || null;

  const Header = (
    <div className="flex justify-between items-start mb-6">
      <div>
        <h1 className="text-2xl font-bold text-fg">Settings</h1>
        <p className="text-fg-muted text-sm mt-1">
          All LatentRouter configuration. Values come from the DB when set, otherwise the built-in default.
          Edits propagate to every latent_router process within one TTL window — no restart.
        </p>
      </div>
      <button onClick={load} className="flex items-center gap-2 px-3 py-2 bg-panel border border-panel-border text-fg-muted hover:text-fg rounded-lg text-sm"><RefreshCw className="w-4 h-4" />Refresh</button>
    </div>
  );

  const Message = message && (
    <div className={`mb-4 p-3 rounded-lg text-sm flex justify-between ${message.type === 'success' ? 'bg-green-500/10 border border-green-500/20 text-green-400' : 'bg-red-500/10 border border-red-500/20 text-red-400'}`}>
      <span>{message.text}</span>
      <button onClick={() => setMessage(null)} className="opacity-70 hover:opacity-100">✕</button>
    </div>
  );

  if (loading) return (<div>{Header}<div className="text-center text-fg-muted py-16">Loading…</div></div>);

  if (current) {
    const Icon = (CARD_META[current.name] || {}).icon || SlidersHorizontal;
    return (
      <ModelsDevContext.Provider value={data.options.models_dev !== false}>
      <div>
        {Header}
        {Message}
        <button onClick={() => setSelected(null)} className="flex items-center gap-1 text-sm text-fg-muted hover:text-fg mb-4">
          <ChevronLeft className="w-4 h-4" /> All settings
        </button>
        <div className="flex items-center gap-3 mb-5">
          <div className="p-2.5 rounded-lg bg-orange/10"><Icon className="w-5 h-5 text-orange" /></div>
          <div>
            <h2 className="text-xl font-semibold text-fg">{current.name}</h2>
            <p className="text-fg-muted text-xs">{(CARD_META[current.name] || {}).desc}</p>
          </div>
        </div>

        <div className="space-y-6">
          {current.name === 'Model Settings' && <ModelsDevSwitch options={data.options} onToggled={onModelsDevToggled} />}
          {current.scalars.length > 0 && (
            <div className="space-y-3">
              {current.scalars.map((s) => <ScalarRow key={s.key} s={s} onChanged={onChanged} />)}
            </div>
          )}
          {(() => {
            // Union of ids across sibling by_key groups sharing one id field, so
            // Pricing and Model Capabilities list the same models.
            const sharedByField = {};
            for (const g of current.groups) {
              if (g.shape !== 'by_key' || (g.id_fields || []).length !== 1) continue;
              const f = g.id_fields[0].field;
              const set = sharedByField[f] || (sharedByField[f] = new Set());
              Object.keys(g.defaults || {}).forEach((id) => set.add(id));
              Object.keys(g.override || {}).forEach((id) => set.add(id));
            }
            const provGroup = data.groups.find((x) => x.key === 'providers');
            // A tombstoned override deletes a built-in, so its name must leave every list that
            // offers a provider — it stayed selectable and failed with "Unknown provider".
            const deletedProviders = new Set(Object.entries(provGroup?.override || {})
              .filter(([, row]) => row && row.__deleted).map(([id]) => id));
            const providerNames = provGroup
              ? [...new Set([...Object.keys(provGroup.defaults || {}), ...Object.keys(provGroup.override || {})])]
                  .filter((id) => !deletedProviders.has(id)).sort()
              : [];
            // From the router, so the panel cannot offer a control it would reject.
            const dyn = { providers: providerNames, ...(data.options || {}) };
            return current.groups.map((g) => {
              const single = g.shape === 'by_key' && (g.id_fields || []).length === 1;
              const extraIds = single ? [...(sharedByField[g.id_fields[0].field] || [])] : [];
              // Only providers have dependants worth naming: a model row points at
              // one by `provider` and by every `host_id` key. From every group, not this card's:
              // Providers and Models are separate cards, so the lookup always came back empty.
              const referencedBy = g.key === 'providers'
                ? data.groups.find((x) => x.key === 'models') : null;
              return <GroupSection key={g.key} group={g} onChanged={onChanged} extraIds={extraIds}
                dynamicOptions={dyn} referencedBy={referencedBy} />;
            });
          })()}
        </div>
      </div>
      </ModelsDevContext.Provider>
    );
  }

  return (
    <div>
      {Header}
      {Message}
      <div className="p-3 bg-blue-500/10 border border-blue-500/20 rounded-lg text-blue-400 text-sm flex items-start gap-2 mb-6">
        <Info className="w-4 h-4 mt-0.5 shrink-0" />
        <span>Stored in <strong>{meta.db || 'latent_router'}.{meta.collection || 'latent_router_config'}</strong>. Pick a category to edit its settings. Values not overridden use latent_router's built-in defaults.</span>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {cards.map((card) => {
          const Icon = (CARD_META[card.name] || {}).icon || SlidersHorizontal;
          return (
            <button
              key={card.name}
              onClick={() => setSelected(card.name)}
              className="text-left bg-panel border border-panel-border rounded-xl p-5 hover:border-orange/40 transition-colors group"
            >
              <div className="flex items-start justify-between">
                <div className="p-2.5 rounded-lg bg-orange/10"><Icon className="w-5 h-5 text-orange" /></div>
                <ChevronRight className="w-4 h-4 text-fg-muted group-hover:text-fg" />
              </div>
              <h3 className="text-fg font-semibold mt-3">{card.name}</h3>
              <p className="text-fg-muted text-xs mt-1 leading-relaxed min-h-[2rem]">{(CARD_META[card.name] || {}).desc}</p>
              <div className="flex items-center gap-2 mt-3 text-xs">
                <span className="text-fg-muted">{card.count} setting{card.count !== 1 ? 's' : ''}</span>
                {card.overridden > 0 && (
                  <span className="px-1.5 py-0.5 bg-blue-500/20 text-blue-400 rounded">{card.overridden} overridden</span>
                )}
              </div>
            </button>
          );
        })}

        <button
          onClick={() => navigate('/prompts')}
          className="text-left bg-panel border border-panel-border rounded-xl p-5 hover:border-orange/40 transition-colors group"
        >
          <div className="flex items-start justify-between">
            <div className="p-2.5 rounded-lg bg-orange/10"><MessageSquareText className="w-5 h-5 text-orange" /></div>
            <ChevronRight className="w-4 h-4 text-fg-muted group-hover:text-fg" />
          </div>
          <h3 className="text-fg font-semibold mt-3">Prompt Management</h3>
          <p className="text-fg-muted text-xs mt-1 leading-relaxed min-h-[2rem]">Default system prompt + per-model overrides, prepended to every request.</p>
          <div className="flex items-center gap-2 mt-3 text-xs">
            <span className="text-fg-muted">Open manager</span>
          </div>
        </button>

        {/* Local-only tool, hidden where the deployment disables it */}
        {flags.harness_eval && <button
          onClick={() => navigate('/harness-eval')}
          className="text-left bg-panel border border-panel-border rounded-xl p-5 hover:border-orange/40 transition-colors group"
        >
          <div className="flex items-start justify-between">
            <div className="p-2.5 rounded-lg bg-orange/10"><FlaskConical className="w-5 h-5 text-orange" /></div>
            <ChevronRight className="w-4 h-4 text-fg-muted group-hover:text-fg" />
          </div>
          <h3 className="text-fg font-semibold mt-3">Harness Eval</h3>
          <p className="text-fg-muted text-xs mt-1 leading-relaxed min-h-[2rem]">Benchmark LatentCode and Claude Code on the same tasks — accuracy, cost and tokens.</p>
          <div className="flex items-center gap-2 mt-3 text-xs">
            <span className="text-fg-muted">Open runner</span>
          </div>
        </button>}
      </div>
    </div>
  );
}
